import React, { useState, useRef, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { useEtatGlobal } from "../../EtatGlobal.js";
import { httpClient, authFetch } from "../../../HttpClient";
import { uploadFileToBlob } from "../../../utils/uploadFileToBlob";
import { jsPDF } from "jspdf";
import { IoIosSearch } from "react-icons/io";
import { CiFilter } from "react-icons/ci";
import OverlayDetails from "./Bilan/OverlayDetails";
import DiligencesLabAnnee from "./Bilan/DiligencesLabAnnee";
import CreateTodo from "./JuridiquePonctuelle/createTodo.js";
import FicheClientOverlay from "../../Portefeuille/FicheClientOverlay";
import { resolveEquipeForYear } from "../../Portefeuille/resolveEquipeAnnee.js";

import {
  ChevronDown,
  ArrowLeft,
  Save,
  Shield,
  MessageSquare,
  FileText,
  FileSpreadsheet,
  Users,
  Briefcase,
  Calendar,
  History,
  Clock,
  Plus,
  ExternalLink,
  CheckCircle2,
  AlertTriangle,
  X,
  AlertCircle,
  Loader2,
  Mail,
  Send,
} from "lucide-react";

import { isPast, parseISO, startOfDay } from "date-fns";
import { missionCommencee, missionActive, estBloque } from "./suiviProdUtils";
import BadgeBloque from "./BadgeBloque";
import Claude from "../../claude/claude.js";
import { CHAMPS_LIAISON } from "../../Portefeuille/LiaisonAcd";

import { dialog } from "../../../components/ui/Dialog";
import { showStatusToast } from "../../../components/ui/Toast";
import "../../ToDo/ToDo.css";

const LAB_QUESTIONS = [
  "Le cabinet a-t-il rencontré le client au cours de la mission pour confirmer ses besoins et maintenir sa connaissance de l'entreprise ?",
  "La répartition des travaux comptables entre le client et le cabinet a-t-elle changée ?",
  "Le volume d'écritures comptables a-t-il été collecté ?",
  "Les spécificités comptables, fiscales et sociales relatives à l'activité et pouvant nécessiter des travaux approfondis ou spécifiques (valorisation, détermination de provisions, etc.) ont-elles été collectées ?",
  "Les délais spécifiques à respecter (demande particulière du client...) ont-ils été collectés ?",
  "Le cabinet est-il indépendant vis-à-vis du client ?",
  "Le cabinet a-t-il la compétence pour réaliser cette mission ?",
  "Le cabinet dispose-t-il des moyens adéquats pour assurer cette mission dans de bonnes conditions (notamment de délai) ?",
  "Existe-t-il une opposition à notre maintien en fonction ?",
];

const LAB_QUESTIONS_COURTES = [
  "Rencontre du client et connaissance de l'entreprise",
  "Changement de la répartition des travaux",
  "Collecte du volume d'écritures",
  "Collecte des spécificités comptables, fiscales et sociales",
  "Collecte des délais spécifiques",
  "Indépendance du cabinet",
  "Compétence du cabinet",
  "Moyens adéquats du cabinet",
  "Opposition au maintien en fonction",
];
const STATUTS_ENVOI_REVUE = {
  en_attente: { label: "En attente", style: "bg-amber-50 text-amber-700 border-amber-200" },
  validee: { label: "Validée", style: "bg-green-50 text-green-700 border-green-200" },
  refusee: { label: "Non validée", style: "bg-red-50 text-red-600 border-red-200" },
  remplacee: { label: "Remplacée par un renvoi", style: "bg-slate-50 text-slate-500 border-slate-200" },
  echec_envoi: { label: "Mail non parti", style: "bg-rose-50 text-rose-600 border-rose-200" },
};
const dateHeureFr = (d) =>
  d ? `${new Date(d).toLocaleDateString("fr-FR")} à ${new Date(d).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" }).replace(":", " h ")}` : "";

// Avancement de la revue annuelle LAB : les 9 questions + la conclusion
// (niveau de risque, décision, synthèse) — tout est requis avant l'envoi à
// l'expert-comptable (demande du 15/10/2026).
function labProgression({ labAnswers, labRisque, labDecision, labSynthese }) {
  const questions = LAB_QUESTIONS.filter((_, i) => (labAnswers || {})[`q${i + 1}`]).length;
  const conclusion = { risque: !!labRisque, decision: !!labDecision, synthese: !!(labSynthese || "").trim() };
  const faits = questions + Object.values(conclusion).filter(Boolean).length;
  const total = LAB_QUESTIONS.length + 3;
  const restantes = LAB_QUESTIONS.length - questions;
  const manquants = [
    restantes && `${restantes} question${restantes > 1 ? "s" : ""}`,
    !conclusion.risque && "le niveau de risque",
    !conclusion.decision && "la décision sur la mission",
    !conclusion.synthese && "la synthèse",
  ].filter(Boolean);
  return { faits, total, complet: faits === total, manquants };
}
const listeFr = (l) => (l.length > 1 ? `${l.slice(0, -1).join(", ")} et ${l[l.length - 1]}` : l[0] || "");

const TABLE_FILTERABLE_COLUMNS = {
  liasseFiscale: { label: "Liasse Fiscale", type: "booleanX" },
  cvae1330: { label: "CVAE 1330", type: "booleanX" },
  rci2069: { label: "RCI 2069", type: "booleanX" },
  s2079: { label: "S2079", type: "booleanX" },
  s2082: { label: "S2082", type: "booleanX" },
  s2083: { label: "S2083", type: "booleanX" },
  oga: { label: "OGA", type: "booleanX" },
  decLoyer: { label: "Déclaration Loyer", type: "booleanX" },
  tdfccheck: { label: "TDFC", type: "booleanX" },
  s2572check: { label: "S2572", type: "booleanX" },
  s3517check: { label: "S3517", type: "booleanX" },
  def1329check: { label: "DEF1329", type: "booleanX" },
  status: { label: "Status", type: "string" },
};

// Rôle "de terrain" : ne voit que ses dossiers (collaborateur niv.1 ou niv.2
// pour l'année affichée) et ceux de son équipe dans la hiérarchie — même
// règle que les Traitements comptables (SuiviTraitementsComptables.js).
// Demande pour Reine-Claude RIVIERE (13/10/2026) : elle voyait tous les
// collaborateurs du cabinet.
const ROLES_VISIBILITE_RESTREINTE = ["COLLABORATEUR"];

const FILTERABLE_BUTTON_COLUMNS = [
  "formeSociale",
  "regimeFiscal",
  "regimeTVA",
  "status",
];

function generateLabRevuePDF({
  clientNom,
  annee,
  labAnswers,
  labRisque,
  labDecision,
  labSynthese,
  labSignataire,
  validatedAt,
  users,
  // Envoi à l'expert-comptable (15/10/2026) : la revue n'est pas encore
  // validée, le PDF l'indique ; il est déposé dans le stockage, pas téléchargé.
  soumission = false,
  telecharger = true,
}) {
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const PW = 210,
    PH = 297,
    ML = 18,
    MR = 18,
    CW = PW - ML - MR;

  const signataireName = users?.find((u) => u.email === labSignataire)
    ? `${users.find((u) => u.email === labSignataire).lastName} ${
        users.find((u) => u.email === labSignataire).name
      }`
    : labSignataire || "";

  const dateStr = validatedAt
    ? new Date(validatedAt).toLocaleDateString("fr-FR", {
        day: "2-digit",
        month: "long",
        year: "numeric",
      })
    : new Date().toLocaleDateString("fr-FR", {
        day: "2-digit",
        month: "long",
        year: "numeric",
      });

  const C = {
    brand: [132, 0, 64],
    dark: [20, 20, 20],
    muted: [120, 120, 120],
    border: [220, 220, 220],
    bg: [248, 248, 249],
    green: [16, 124, 65],
    red: [185, 28, 28],
    amber: [180, 100, 0],
  };

  function pageHeader(title, subtitle) {
    doc.setFillColor(255, 255, 255);
    doc.rect(0, 0, PW, PH, "F");

    if (signataireName) {
      doc.setFontSize(8);
      doc.setTextColor(...C.brand);
      doc.setFont("helvetica", "normal");
      doc.text(`${soumission ? "Soumise à la validation de" : "Validé par"} ${signataireName} le ${dateStr}`, PW - MR, 13, {
        align: "right",
      });
    }

    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...C.dark);
    doc.text(title, ML, 22);

    if (subtitle) {
      doc.setFontSize(9);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(...C.muted);
      doc.text(subtitle, ML, 30);
    }

    doc.setDrawColor(...C.border);
    doc.setLineWidth(0.5);
    doc.line(ML, 35, PW - MR, 35);

    return 43;
  }

  function questionRow(y, num, text, reponse, commentaire) {
    const qW = CW - 32;
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    const lines = doc.splitTextToSize(text, qW);
    const rowH = Math.max(lines.length * 4.8 + 8, 14);

    if (y + rowH > PH - 18) {
      doc.addPage();
      doc.setFillColor(255, 255, 255);
      doc.rect(0, 0, PW, PH, "F");
      y = 20;
    }

    if (num % 2 === 0) {
      doc.setFillColor(...C.bg);
      doc.rect(ML, y, CW, rowH, "F");
    }

    doc.setFontSize(8);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...C.brand);
    doc.text(String(num).padStart(2, "0"), ML + 3, y + rowH / 2 + 1.5);

    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...C.dark);
    doc.text(lines, ML + 11, y + (rowH - lines.length * 4.8) / 2 + 4);

    const rColor =
      reponse === "Oui" ? C.green : reponse === "Non" ? C.red : C.muted;
    doc.setFontSize(9);
    doc.setFont("helvetica", reponse ? "bold" : "normal");
    doc.setTextColor(...rColor);
    doc.text(reponse || "—", PW - MR, y + rowH / 2 + 1.5, { align: "right" });

    doc.setDrawColor(...C.border);
    doc.setLineWidth(0.15);
    doc.line(ML, y + rowH, PW - MR, y + rowH);

    let nextY = y + rowH;
    if (commentaire) {
      doc.setFontSize(8);
      doc.setFont("helvetica", "italic");
      doc.setTextColor(...C.muted);
      const cLines = doc.splitTextToSize(`↳ ${commentaire}`, CW - 14);
      doc.text(cLines, ML + 11, nextY + 3.5);
      nextY += cLines.length * 4 + 6;
    }

    return nextY + 1;
  }

  // ── Page 1 : Questionnaire de maintien de mission ───────────────────────────
  let y = pageHeader(
    `Revue annuelle — ${(clientNom || "").toUpperCase()}`,
    `Exercice ${annee}  ·  Questionnaire de maintien de mission`
  );

  doc.setFontSize(11);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...C.dark);
  doc.text("Questionnaire de maintien de mission", ML, y);
  y += 2;
  doc.setDrawColor(...C.border);
  doc.setLineWidth(0.3);
  doc.line(ML, y, PW - MR, y);
  y += 8;

  LAB_QUESTIONS.forEach((question, idx) => {
    const key = `q${idx + 1}`;
    const reponse = labAnswers[key] || null;
    const commentaire = labAnswers[`${key}_note`] || "";
    y = questionRow(y, idx + 1, question, reponse, commentaire);
  });

  // ── Page 2 : Conclusion + Validation ───────────────────────────────────────
  doc.addPage();
  y = pageHeader(
    "Conclusion de la revue annuelle",
    `Exercice ${annee}  ·  ${clientNom || ""}`
  );

  // Niveau de risque
  const risqueColor =
    labRisque === "Faible"
      ? C.green
      : labRisque === "Modéré"
      ? C.amber
      : labRisque === "Élevé"
      ? C.red
      : C.muted;
  doc.setFillColor(...C.bg);
  doc.rect(ML, y, CW, 14, "F");
  doc.setFontSize(9);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(...C.muted);
  doc.text("Niveau de risque résiduel", ML + 4, y + 9);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...risqueColor);
  doc.text(labRisque || "Non renseigné", PW - MR, y + 9, { align: "right" });
  y += 16;

  // Décision mission
  const decisionColor =
    labDecision === "Maintien"
      ? C.green
      : labDecision === "Résiliation"
      ? C.red
      : C.amber;
  doc.setFillColor(255, 255, 255);
  doc.rect(ML, y, CW, 14, "F");
  doc.setFontSize(9);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(...C.muted);
  doc.text("Décision sur la mission", ML + 4, y + 9);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...(labDecision ? decisionColor : C.muted));
  doc.text(labDecision || "Non renseigné", PW - MR, y + 9, { align: "right" });
  y += 16;

  doc.setDrawColor(...C.border);
  doc.setLineWidth(0.3);
  doc.line(ML, y, PW - MR, y);
  y += 8;

  // Synthèse
  if (labSynthese) {
    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...C.dark);
    doc.text("Synthèse", ML, y);
    y += 6;
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...C.dark);
    const synLines = doc.splitTextToSize(labSynthese, CW);
    doc.text(synLines, ML, y);
    y += synLines.length * 5 + 10;
  }

  doc.setDrawColor(...C.border);
  doc.setLineWidth(0.3);
  doc.line(ML, y, PW - MR, y);
  y += 10;

  // Validation
  doc.setFontSize(11);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...C.dark);
  doc.text("Validation par l'expert-comptable signataire", ML, y);
  y += 8;

  doc.setFillColor(...C.bg);
  doc.roundedRect(ML, y, CW, 22, 2, 2, "F");
  doc.setFontSize(9);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(...C.muted);
  doc.text("Expert-comptable signataire", ML + 6, y + 8);
  doc.text(soumission ? "Soumise le" : "Date de validation", ML + 6, y + 16);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...C.dark);
  doc.text(signataireName || "—", PW - MR, y + 8, { align: "right" });
  doc.text(dateStr, PW - MR, y + 16, { align: "right" });

  y += 30;
  if (soumission) {
    doc.setFontSize(9);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...C.amber);
    doc.text("En attente de validation par l'expert-comptable signataire", ML, y);
    y += 8;
  }
  doc.setFontSize(8);
  doc.setFont("helvetica", "italic");
  doc.setTextColor(...C.brand);
  doc.text("Document généré par MyALFA WorkSync — Alfa Partenaires", ML, y);

  const fileName = `LAB-Revue-${(clientNom || "dossier").replace(
    /\s+/g,
    "-"
  )}-${annee}-${new Date().toISOString().slice(0, 10)}.pdf`;
  if (telecharger) doc.save(fileName);
  return { blob: doc.output("blob"), fileName };
}

const SuiviBilan = ({ suiviBilanData, loading, refetch, refetchAGO }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedClient, setSelectedClient] = useState(null);

  const [formData, setFormData] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [overlayData, setOverlayData] = useState(null);
  const [ficheClientOpen, setFicheClientOpen] = useState(false);
  const [ficheClientData, setFicheClientData] = useState(null);
  const [ficheClientLoading, setFicheClientLoading] = useState(false);
  const ficheClientCache = useRef({});
  const {
    utilisateur,
    users,
    setUserDashboardData,
    fetchUserDashboard,
    patchSuiviBilanYear,
    patchSuiviJuridiqueAnnee,
    patchSuiviBilanClient,
    todos: blocsHierarchie,
  } = useEtatGlobal();
  const [activeSection, setActiveSection] = useState("Révision");
  const [newCommentTexts, setNewCommentTexts] = useState({});
  const [isAddingComment, setIsAddingComment] = useState(false);
  const [showCreateTodo, setShowCreateTodo] = useState(false); // nouvel état
  const [initialStatus, setInitialStatus] = useState(null);
  const [toggledComments, setToggledComments] = useState({});
  const [selectedCollaborateur, setSelectedCollaborateur] = useState(null);
  const [openGroups, setOpenGroups] = React.useState({});
  const [activeColumnFilter, setActiveColumnFilter] = useState(null);
  const [columnFilterPosition, setColumnFilterPosition] = useState(null);
  const [uniqueOptionsByFilter, setUniqueOptionsByFilter] = useState({});
  const [localSuiviBilanData, setLocalSuiviBilanData] =
    useState(suiviBilanData);

  const [selectedCommentType, setSelectedCommentType] = useState(null);

  const [annexeOpen2, setAnnexeOpen2] = useState(false);
  const [annexeOpen, setAnnexeOpen] = useState(false);

  const safeFormData = formData || {};

  const filterRef = useRef(null);

  const [labAnswers, setLabAnswers] = useState({});
  const [labExpandedQ, setLabExpandedQ] = useState(null);
  const [labFileUploading, setLabFileUploading] = useState({});
  const [labUploadProgress, setLabUploadProgress] = useState({});
  const [labRisque, setLabRisque] = useState(null);
  const [labDecision, setLabDecision] = useState(null);
  const [labSynthese, setLabSynthese] = useState("");
  const [labSignataire, setLabSignataire] = useState("");
  const [isValidating, setIsValidating] = useState(false);
  const [toast, setToast] = useState(null);
  const [animatingId, setAnimatingId] = useState(null);

  const showToast = (message, type = "success") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const totalChecked = [
    safeFormData.microEntreprise,
    safeFormData.personnePhysique,
    safeFormData.petiteEntreprise,
    safeFormData.abregee,
  ].filter(Boolean).length;

  const totalChecked2 = [
    safeFormData.casGeneral,
    safeFormData.organismeFormation,
    safeFormData.petitGroupe,
    safeFormData.nA,
  ].filter(Boolean).length;

  const sections = [
    "Révision",
    "Vérification seuil",
    "RDV Client",
    "Déclarations fiscales",
    "CRM",
    "Mission complémentaire",
    "Diligences LAB",
    "Historique",
  ];

  const sectionIcons = {
    "Diligences LAB": Shield,
    Révision: MessageSquare,
    "Déclarations fiscales": FileText,
    "Vérification seuil": FileSpreadsheet,
    CRM: Users,
    "Mission complémentaire": Briefcase,
    "RDV Client": Calendar,
    Historique: History,
  };

  const itemtype = "SuiviBilan";

  const [filterRows, setFilterRows] = useState([
    { column: "formeSociale", selectedValues: [] },
  ]);

  const [showFilterOverlay, setShowFilterOverlay] = useState(false);
  const overlayRef = useRef(null);
  const initialStateRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (overlayRef.current && !overlayRef.current.contains(event.target)) {
        setShowFilterOverlay(false);
      }
    };

    if (showFilterOverlay) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showFilterOverlay]);

  const [selectedYear, setSelectedYear] = useState(2025);

  // Revue annuelle LAB envoyée à l'expert-comptable (statut, dates) :
  // backend controllers/Suivi/revueLabController.js.
  const [revueEnvoi, setRevueEnvoi] = useState(null);
  const [revueEnvois, setRevueEnvois] = useState([]);
  const [revueOverlay, setRevueOverlay] = useState(false);
  const suiviBilanIdLab = selectedClient?.client?.suiviBilans?.find((s) => s.annees === selectedYear)?.id;
  useEffect(() => {
    setRevueEnvoi(null);
    setRevueEnvois([]);
    if (activeSection !== "Diligences LAB" || !suiviBilanIdLab) return undefined;
    let actif = true;
    httpClient
      .get(`https://backend-myalfa.vercel.app/api/suiviBilan/${suiviBilanIdLab}/revue-lab`)
      .then(({ data }) => {
        if (!actif) return;
        setRevueEnvoi(data?.envoi || null);
        setRevueEnvois(Array.isArray(data?.envois) ? data.envois : data?.envoi ? [data.envoi] : []);
      })
      .catch(() => {});
    return () => {
      actif = false;
    };
  }, [activeSection, suiviBilanIdLab]);

  // Liaison ACD faite ailleurs (fiche client ouverte depuis ce tableau ou le
  // Portefeuille) : le dossier sélectionné — donc le CRM — suit le tableau.
  useEffect(() => {
    const id = selectedClient?.client?.id;
    if (!id) return;
    const ligne = (suiviBilanData || []).find((c) => c.id === id);
    if (!ligne) return;
    const changes = CHAMPS_LIAISON.filter((k) => k in ligne && ligne[k] !== selectedClient.client[k]);
    if (!changes.length) return;
    const patch = Object.fromEntries(changes.map((k) => [k, ligne[k]]));
    setSelectedClient((prev) => (prev && prev.client?.id === id ? { ...prev, client: { ...prev.client, ...patch } } : prev));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suiviBilanData]);


  useEffect(() => {
    if (selectedClient) {
      const currentBilan =
        selectedClient?.client?.suiviBilans?.find(
          (bilan) => bilan.annees === selectedYear
        ) || null;

      setFormData(
        currentBilan
          ? {
              status: currentBilan.status,
              kanta: currentBilan.kanta,
              lettreMisson: currentBilan.lettreMisson,
              lAB: currentBilan.lAB,
              maintientMission: currentBilan.maintientMission,
              ficheRevue: currentBilan.ficheRevue,
              rdvClient: currentBilan.rdvClient,
              s3517check: currentBilan.s3517check,
              s3517: currentBilan.s3517,
              s3517ar: currentBilan.s3517ar,
              s3517Ligne: currentBilan.s3517Ligne,
              s3517Montant: currentBilan.s3517Montant,
              s2572check: currentBilan.s2572check,
              s2572: currentBilan.s2572,
              s2572ar: currentBilan.s2572ar,
              s2572Ligne: currentBilan.s2572Ligne,
              s2572Montant: currentBilan.s2572Montant,
              s3573check: currentBilan.s3573check,
              s3573: currentBilan.s3573,
              s3573ar: currentBilan.s3573ar,
              def1329check: currentBilan.def1329check,
              def1329: currentBilan.def1329,
              def1329ar: currentBilan.def1329ar,
              liasseFiscale: currentBilan.liasseFiscale,
              cvae1330: currentBilan.cvae1330,
              s2082: currentBilan.s2082,
              s2083: currentBilan.s2083,
              sexies44: currentBilan.sexies44,
              rci2069: currentBilan.rci2069,
              s2079: currentBilan.s2079,
              decloyer: currentBilan.decLoyer,
              oga: currentBilan.oga,
              tdfc: currentBilan.tdfc,
              tdfccheck: currentBilan.tdfccheck,
              tdfcar: currentBilan.tdfcar,
              reserveLegale: currentBilan.reserveLegale,
              reserveLegaleMontant: currentBilan.reserveLegaleMontant,
              dotee: currentBilan.dotee,
              dispense: currentBilan.dispense,
              sarl: currentBilan.sarl,
              aurtres: currentBilan.aurtres,
              report: currentBilan.report,
              remuneration: currentBilan.remuneration,
              convention: currentBilan.convention,
              microEntreprise: currentBilan.microEntreprise,
              personnePhysique: currentBilan.personnePhysique,
              petiteEntreprise: currentBilan.petiteEntreprise,
              abregee: currentBilan.abregee,
              nA: currentBilan.nA,
              casGeneral: currentBilan.casGeneral,
              organismeFormation: currentBilan.organismeFormation,
              petitGroupe: currentBilan.petitGroupe,
              missionComplementaire: currentBilan.missionComplementaire,
              dueDate: currentBilan.dueDate,
            }
          : {}
      );
      const q = currentBilan?.questionnaireMaintenMission || {};
      setLabAnswers(q);
      setLabExpandedQ(null);
      setLabFileUploading({});
      setLabRisque(currentBilan?.labRisque || null);
      setLabDecision(currentBilan?.labDecision || null);
      setLabSynthese(currentBilan?.labSynthese || "");
      setLabSignataire(currentBilan?.labSignataire || "");

      initialStateRef.current = {
        formData: currentBilan
          ? {
              status: currentBilan.status,
              kanta: currentBilan.kanta,
              lettreMisson: currentBilan.lettreMisson,
              lAB: currentBilan.lAB,
              maintientMission: currentBilan.maintientMission,
              ficheRevue: currentBilan.ficheRevue,
              rdvClient: currentBilan.rdvClient,
              s3517check: currentBilan.s3517check,
              s3517: currentBilan.s3517,
              s3517ar: currentBilan.s3517ar,
              s3517Ligne: currentBilan.s3517Ligne,
              s3517Montant: currentBilan.s3517Montant,
              s2572check: currentBilan.s2572check,
              s2572: currentBilan.s2572,
              s2572ar: currentBilan.s2572ar,
              s2572Ligne: currentBilan.s2572Ligne,
              s2572Montant: currentBilan.s2572Montant,
              s3573check: currentBilan.s3573check,
              s3573: currentBilan.s3573,
              s3573ar: currentBilan.s3573ar,
              def1329check: currentBilan.def1329check,
              def1329: currentBilan.def1329,
              def1329ar: currentBilan.def1329ar,
              liasseFiscale: currentBilan.liasseFiscale,
              cvae1330: currentBilan.cvae1330,
              s2082: currentBilan.s2082,
              s2083: currentBilan.s2083,
              sexies44: currentBilan.sexies44,
              rci2069: currentBilan.rci2069,
              s2079: currentBilan.s2079,
              decloyer: currentBilan.decLoyer,
              oga: currentBilan.oga,
              tdfc: currentBilan.tdfc,
              tdfccheck: currentBilan.tdfccheck,
              tdfcar: currentBilan.tdfcar,
              reserveLegale: currentBilan.reserveLegale,
              reserveLegaleMontant: currentBilan.reserveLegaleMontant,
              dotee: currentBilan.dotee,
              dispense: currentBilan.dispense,
              sarl: currentBilan.sarl,
              aurtres: currentBilan.aurtres,
              report: currentBilan.report,
              remuneration: currentBilan.remuneration,
              convention: currentBilan.convention,
              microEntreprise: currentBilan.microEntreprise,
              personnePhysique: currentBilan.personnePhysique,
              petiteEntreprise: currentBilan.petiteEntreprise,
              abregee: currentBilan.abregee,
              nA: currentBilan.nA,
              casGeneral: currentBilan.casGeneral,
              organismeFormation: currentBilan.organismeFormation,
              petitGroupe: currentBilan.petitGroupe,
              missionComplementaire: currentBilan.missionComplementaire,
              dueDate: currentBilan.dueDate,
            }
          : {},
        labAnswers: currentBilan?.questionnaireMaintenMission || {},
        labRisque: currentBilan?.labRisque || null,
        labDecision: currentBilan?.labDecision || null,
        labSynthese: currentBilan?.labSynthese || "",
        labSignataire: currentBilan?.labSignataire || "",
      };
    }
  }, [selectedClient, selectedYear]);

  // Dossiers visibles selon le rôle (voir ROLES_VISIBILITE_RESTREINTE) —
  // base de toutes les listes et compteurs de l'écran.
  const visibiliteRestreinte =
    !!utilisateur &&
    !utilisateur.allCabinetsAccess &&
    ROLES_VISIBILITE_RESTREINTE.includes(utilisateur.role);
  const donneesVisibles = useMemo(() => {
    if (!visibiliteRestreinte) return localSuiviBilanData;
    const perimetre = new Set([
      String(utilisateur.id),
      ...(blocsHierarchie || []).map((b) => String(b.userId)),
    ]);
    return localSuiviBilanData.filter((client) => {
      const equipe = resolveEquipeForYear(client, selectedYear);
      return [equipe.id_collaborateur, equipe.id_collaborateur_niv2].some(
        (id) => id !== null && id !== "" && perimetre.has(String(id)),
      );
    });
  }, [visibiliteRestreinte, localSuiviBilanData, utilisateur, blocsHierarchie, selectedYear]);

  const filteredData = donneesVisibles
    .filter((client) =>
      client.nom.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .filter((client) =>
      filterRows.every((filter) => {
        if (!filter.selectedValues || filter.selectedValues.length === 0)
          return true;
        return filter.selectedValues.includes(client[filter.column]);
      })
    );

  const showClientDetails = async (client) => {
    setFicheClientOpen(true);
    if (ficheClientCache.current[client.id]) {
      setFicheClientData(ficheClientCache.current[client.id]);
      setFicheClientLoading(false);
      return;
    }
    setFicheClientData(client);
    setFicheClientLoading(true);
    try {
      const { data } = await httpClient.get(
        `https://backend-myalfa.vercel.app/api/clients/${client.id}`
      );
      ficheClientCache.current[client.id] = data;
      setFicheClientData(data);
    } catch (err) {
      console.error("Erreur chargement client:", err);
    } finally {
      setFicheClientLoading(false);
    }
  };

  const filteredByYear = useMemo(() => {
    return (
      filteredData
        // Le client n'apparaît qu'à partir de l'année de sa date de début de
        // mission (vide = visible sur toutes les années), et disparaît à
        // partir de l'année suivant sa date d'effet d'archivage si archivé.
        .filter(
          (client) =>
            missionCommencee(client, selectedYear) &&
            missionActive(client, selectedYear),
        )
        .map((client) => {
          // récupérer le suiviBilan correspondant à l'année choisie
          const suivi = client.suiviBilans.find(
            (s) => s.annees === selectedYear
          );
          return { ...client, suiviBilans: suivi ? [suivi] : [] };
        })
    );
  }, [filteredData, selectedYear]);

  const NON_AFFECTE = "__NON_AFFECTE__";

  const isOverdue = (suivi) => {
    if (!suivi?.dueDate) return false;

    return (
      isPast(startOfDay(parseISO(suivi.dueDate))) && suivi.status !== "Finalisé"
    );
  };

  const collaborateursStats = useMemo(() => {
    const map = {};

    const nonAffecte = {
      id: NON_AFFECTE,
      email: null,
      label: "Non affecté",
      total: 0,
      finalise: 0,
      enCours: 0,
      enPreparation: 0,
      enRetard: 0,
      premierBilanN1: 0, // 👈 nouveau compteur
    };

    filteredByYear.forEach((client) => {
      if (estBloque(client)) return;
      // Le collaborateur niv.1 peut être surchargé pour l'année affichée
      // (voir resolveEquipeForYear / ClientEquipeAnnee) — sans ça, changer le
      // collaborateur sur la fiche client réécrirait aussi les années passées.
      const { id_collaborateur, email_collaborateur } = resolveEquipeForYear(
        client,
        selectedYear
      );
      const isNonAffecte =
        id_collaborateur === null || id_collaborateur === "";

      const key = isNonAffecte ? "NON_AFFECTE" : id_collaborateur;

      if (isNonAffecte) {
        nonAffecte.total++;
      } else {
        if (!map[key]) {
          map[key] = {
            id: id_collaborateur,
            email: email_collaborateur,
            total: 0,
            finalise: 0,
            enCours: 0,
            enPreparation: 0,
            enRetard: 0,
            premierBilanN1: 0, // 👈 nouveau compteur
          };
        }
        map[key].total++;
      }

      const target = isNonAffecte ? nonAffecte : map[key];

      client.suiviBilans.forEach((suivi) => {
        // 👇 on capture les 1er bilan N+1
        if (
          suivi.status === "1er bilan en N+1" ||
          suivi.status === "Fin de mission"
        ) {
          target.premierBilanN1++;
          return; // ⚡ on ne les compte nulle part ailleurs
        }

        if (suivi.status === "Finalisé") target.finalise++;
        if (suivi.status === "En préparation") target.enPreparation++;

        if (isOverdue(suivi)) {
          target.enRetard++;
        }
      });

      // 👇 enCours exclut les 1er bilan N+1
      target.enCours = target.total - target.finalise - target.premierBilanN1;
    });

    const result = Object.values(map);

    if (nonAffecte.total > 0) {
      result.push(nonAffecte);
    }

    return result;
  }, [filteredByYear, selectedYear]);

  const handleResetColumnFilter = (column) => {
    setFilterRows((prev) =>
      prev.map((f) => (f.column === column ? { ...f, selectedValues: [] } : f))
    );
  };

  useEffect(() => {
    setLocalSuiviBilanData(suiviBilanData); // sync quand la prop change
  }, [suiviBilanData]);

  const filteredClients = useMemo(() => {
    return (
      donneesVisibles
        // 🔹 Filtre par recherche
        .filter((client) =>
          client.nom.toLowerCase().includes(searchTerm.toLowerCase())
        )
        // 🔹 Filtre par filtres dynamiques (formeSociale, regimeFiscal, etc.)
        .filter((client) =>
          filterRows.every((filter) => {
            if (!filter.selectedValues?.length) return true;

            const columnConfig = TABLE_FILTERABLE_COLUMNS[filter.column];

            // 🔹 BooleanX
            if (columnConfig?.type === "booleanX") {
              return client.suiviBilans.some(
                (s) =>
                  s[filter.column] === true &&
                  filter.selectedValues.includes("Oui")
              );
            }

            // 🔹 String venant de suiviBilans (status)
            if (columnConfig?.type === "string") {
              return client.suiviBilans.some((s) =>
                filter.selectedValues.includes(s[filter.column])
              );
            }

            // 🔹 Colonnes classiques client
            let clientValue = client[filter.column];
            if (Array.isArray(clientValue)) clientValue = clientValue[0];
            return filter.selectedValues.includes(clientValue);
          })
        )
        // 🔹 Filtre par collaborateur (résolu pour l'année affichée)
        .filter((client) => {
          if (selectedCollaborateur === null) return true;

          const { id_collaborateur } = resolveEquipeForYear(client, selectedYear);

          if (selectedCollaborateur === NON_AFFECTE) {
            return id_collaborateur === null || id_collaborateur === "";
          }

          return id_collaborateur === selectedCollaborateur;
        })
        // 🔹 Filtre par année
        .map((client) => {
          let suivi = client.suiviBilans.find((s) => s.annees === selectedYear);

          // Si on a un suivi temporaire dans local state (optimistic update)
          if (!suivi) {
            suivi = {
              annees: selectedYear,
              status: "", // valeur par défaut
              liasseFiscale: false,
              cvae1330: false,
              rci2069: false,
              s2079: false,
              s2082: false,
              s2083: false,
              oga: false,
              decLoyer: false,
              tdfccheck: false,
              s2572check: false,
              s3517check: false,
              def1329check: false,
            };
          }

          return { ...client, suiviBilans: [suivi] };
        })
        .filter(Boolean)
    );
  }, [
    donneesVisibles,
    searchTerm,
    filterRows,
    selectedCollaborateur,
    selectedYear,
  ]);

  const availableYears = useMemo(() => {
    if (!suiviBilanData || suiviBilanData.length === 0) return [];

    const minYear = Math.min(
      ...suiviBilanData.map((c) => new Date(c.dateCloture).getFullYear())
    );
    const maxYear = new Date().getFullYear() + 2; // tu peux adapter

    const years = [];
    for (let y = minYear; y <= maxYear; y++) {
      years.push(y);
    }
    return years;
  }, [suiviBilanData]);

  const uniqueValues = useMemo(() => {
    const values = {
      formeSociale: [...new Set(suiviBilanData.map((c) => c.formeSociale))],
      regimeFiscal: [...new Set(suiviBilanData.map((c) => c.regimeFiscal))],
      regimeTVA: [...new Set(suiviBilanData.map((c) => c.regimeTVA))],

      // ✅ status = valeurs dynamiques
      status: [
        ...new Set(
          suiviBilanData.flatMap((c) =>
            c.suiviBilans.map((s) => s.status).filter(Boolean)
          )
        ),
      ],
    };

    Object.keys(TABLE_FILTERABLE_COLUMNS).forEach((col) => {
      if (TABLE_FILTERABLE_COLUMNS[col].type === "booleanX") {
        values[col] = ["Oui"];
      }
    });

    return values;
  }, [suiviBilanData]);

  const getUniqueValuesForColumn = (column) => {
    const values =
      {
        formeSociale: suiviBilanData.map((c) =>
          Array.isArray(c.formeSociale) ? c.formeSociale[0] : c.formeSociale
        ),
        regimeFiscal: suiviBilanData.map((c) => c.regimeFiscal),
        regimeTVA: suiviBilanData.map((c) => c.regimeTVA),
        status: suiviBilanData.flatMap((c) =>
          c.suiviBilans.map((s) => s.status)
        ),
      }[column] || [];

    // filtre null/undefined/"" et garde uniquement uniques
    return Array.from(new Set(values.filter((v) => v != null && v !== "")));
  };

  // 🔹 Gestion des filtres
  const handleAddFilter = () => {
    setFilterRows((prev) => [
      ...prev,
      { column: "formeSociale", selectedValues: [] },
    ]);
  };

  const handleRemoveFilter = (indexToRemove) => {
    setFilterRows((prev) => prev.filter((_, i) => i !== indexToRemove));
  };

  const handleFilterChange = (index, field, value) => {
    setFilterRows((prev) =>
      prev.map((f, i) =>
        i === index ? { ...f, [field]: value, selectedValues: [] } : f
      )
    );

    // Mettre à jour les options pour ce filtre
    setUniqueOptionsByFilter((prev) => ({
      ...prev,
      [index]: getUniqueValuesForColumn(value),
    }));
  };

  const handleCheckboxValueChange = (index, value) => {
    setFilterRows((prev) =>
      prev.map((f, i) => {
        if (i !== index) return f;
        const selectedValues = f.selectedValues || [];
        const updated = selectedValues.includes(value)
          ? selectedValues.filter((v) => v !== value)
          : [...selectedValues, value];
        return { ...f, selectedValues: updated };
      })
    );
  };

  const handleResetFilters = () => {
    setFilterRows([{ column: "formeSociale", selectedValues: [] }]);
    setSearchTerm("");
  };

  const FilterableTableTh = ({ columnKey, label }) => {
    return (
      <th className="px-1 py-1.5 text-white font-semibold relative text-xs">
        <div className="flex items-center justify-center gap-1">
          {label}
          <button
            onClick={(e) => {
              const rect = e.currentTarget.getBoundingClientRect();
              setColumnFilterPosition({
                top: rect.bottom + 6,
                left: rect.left,
              });
              setActiveColumnFilter(
                activeColumnFilter === columnKey ? null : columnKey
              );
            }}
          >
            <ChevronDown className="w-3 h-3 text-white opacity-80 hover:opacity-100" />
          </button>
        </div>
      </th>
    );
  };

  const toggleColumnValue = (column, value) => {
    setFilterRows((prev) => {
      const existing = prev.find((f) => f.column === column);

      if (!existing) {
        return [...prev, { column, selectedValues: [value] }];
      }

      const alreadySelected = existing.selectedValues.includes(value);

      return prev.map((f) =>
        f.column === column
          ? {
              ...f,
              selectedValues: alreadySelected
                ? f.selectedValues.filter((v) => v !== value)
                : [...f.selectedValues, value],
            }
          : f
      );
    });
  };

  useEffect(() => {
    setSearchTerm("");
  }, [activeColumnFilter]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filterRef.current && !filterRef.current.contains(event.target)) {
        setActiveColumnFilter(null);
      }
    };

    if (activeColumnFilter) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [activeColumnFilter]);

  // 🔹 Filtrage des données

  const getUserNameByEmail = (email) => {
    if (!email) return "";

    const user = users.find((u) => u.email === email);

    // 🟢 Cas 1 : utilisateur trouvé et infos complètes
    if (user?.name && user?.lastName) {
      return `${user.lastName} ${user.name}`;
    }

    // 🟡 Cas 2 : fallback → on découpe l'email
    const emailPart = email.split("@")[0]; // lastName.name
    if (!emailPart.includes(".")) return email;

    const [lastName, name] = emailPart.split(".");

    const format = (str) =>
      str ? str.charAt(0).toUpperCase() + str.slice(1).toLowerCase() : "";

    return `${format(lastName)} ${format(name)}`;
  };

  if (loading)
    return (
      <div>
        <div className="flex items-center justify-between gap-4 mb-5 mt-8 mr-2">
          <div className="h-9 w-64 rounded-md bg-slate-200 animate-pulse" />
          <div className="flex items-center gap-5">
            <div className="h-9 w-20 rounded-md bg-slate-200 animate-pulse" />
            <div className="h-9 w-20 rounded-md bg-slate-200 animate-pulse" />
          </div>
        </div>
        <div className="mt-6 w-full overflow-x-auto border border-slate-200 rounded-lg shadow-sm">
          <table className="w-full" style={{ fontSize: "0.72rem" }}>
            <thead>
              <tr className="bg-[#6e1a36]">
                {[
                  "Dossier",
                  "Status",
                  "Échéance",
                  "RDV Client",
                  "Liasse",
                  "CVAE",
                  "RCI",
                  "S2079",
                  "S2082",
                  "S2083",
                  "OGA",
                  "TDFC",
                  "S2572",
                  "S3517",
                  "DEF1329",
                  "",
                ].map((col, i) => (
                  <th key={i} className="px-2 py-3.5 text-left">
                    <div
                      className="h-3 rounded bg-[#ffffff33] animate-pulse"
                      style={{ width: col ? "80%" : "24px" }}
                    />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {Array.from({ length: 8 }).map((_, i) => (
                <tr key={i} className="bg-white">
                  <td className="px-2 py-3">
                    <div className="h-3 rounded bg-slate-200 animate-pulse w-36" />
                  </td>
                  <td className="px-2 py-3">
                    <div className="h-5 w-20 rounded-full bg-slate-200 animate-pulse" />
                  </td>
                  <td className="px-2 py-3">
                    <div className="h-3 rounded bg-slate-200 animate-pulse w-14" />
                  </td>
                  <td className="px-2 py-3">
                    <div className="h-3 rounded bg-slate-200 animate-pulse w-14" />
                  </td>
                  {Array.from({ length: 11 }).map((__, j) => (
                    <td key={j} className="px-2 py-3 text-center">
                      <div className="h-4 w-4 rounded-full bg-slate-200 animate-pulse mx-auto" />
                    </td>
                  ))}
                  <td className="px-2 py-3">
                    <div className="h-6 w-14 rounded-md bg-slate-200 animate-pulse" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );

  const formatDateWithoutYear = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    if (isNaN(date)) return "";
    const day = date.getDate().toString().padStart(2, "0");
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    return `${day}/${month}`;
  };

  const renderBool = (value) => (value ? "X" : "");

  // Liaison ACD faite depuis le CRM : la sélection, le tableau Suivi Bilan et
  // la fiche client déjà chargée reflètent tout de suite le dossier lié.
  const majClientCrm = (patch) => {
    const id = selectedClient?.client?.id;
    setSelectedClient((prev) => (prev ? { ...prev, client: { ...prev.client, ...patch } } : prev));
    if (!id) return;
    patchSuiviBilanClient?.(id, patch);
    if (ficheClientCache.current[id]) {
      ficheClientCache.current[id] = { ...ficheClientCache.current[id], ...patch };
    }
  };

  // Quand on sélectionne un client, on mémorise aussi le status de départ
  const handleEditClick = (client, suivi) => {
    setSelectedClient({ client, suivi });
    setFormData(suivi ? { ...suivi } : { annees: selectedYear });

    // 🟢 on mémorise le status initial (ou "En préparation" si rien)
    setInitialStatus(suivi?.status || "En préparation");
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const addHistoriqueSuiviBilan = async (nouveauStatus) => {
    try {
      const suiviBilanId = selectedClient?.suivi?.id;
      if (!suiviBilanId) return;

      await httpClient.post(
        "https://backend-myalfa.vercel.app/api/historiqueSuiviBilan",
        {
          titre: "Changement de status",
          description: `Changement de status en - ${nouveauStatus} - par ${utilisateur.lastName} ${utilisateur.name} `,
          suiviBilanId: suiviBilanId,
        }
      );
      console.log("✅ Historique ajouté !");
    } catch (error) {
      console.error("❌ Erreur lors de l’ajout d’historique :", error);
    }
  };

  const handleSubmit = async () => {
    if (!selectedClient) return;

    const clientId = selectedClient.client.id;
    const capturedClient = selectedClient;
    const capturedInitialStatus = initialStatus;
    const newStatus = formData.status;

    // Détecte si seulement le status a changé
    const initial = initialStateRef.current;
    const onlyStatusChanged =
      initial &&
      (() => {
        if (JSON.stringify(labAnswers) !== JSON.stringify(initial.labAnswers))
          return false;
        if (labRisque !== initial.labRisque) return false;
        if (labDecision !== initial.labDecision) return false;
        if (labSynthese !== initial.labSynthese) return false;
        if (labSignataire !== initial.labSignataire) return false;
        const allKeys = new Set([
          ...Object.keys(formData),
          ...Object.keys(initial.formData),
        ]);
        for (const key of allKeys) {
          if (key === "status") continue;
          if (formData[key] !== initial.formData[key]) return false;
        }
        return true;
      })();

    setIsSubmitting(true);

    // 1. Optimistic update immédiat — patché dans EtatGlobal.js (pas
    // seulement l'état local du composant) pour que ça survive à un
    // changement d'onglet (démontage/remontage de l'écran) ; localSuiviBilanData
    // se resynchronise automatiquement dessus via le useEffect existant.
    patchSuiviBilanYear(clientId, selectedYear, formData);

    if (newStatus === "Finalisé") {
      setAnimatingId(clientId);
      setTimeout(() => setAnimatingId(null), 1300);
    }

    // Mise à jour optimiste du dashboard
    const wasFinalisé = capturedInitialStatus === "Finalisé";
    const isFinalisé = newStatus === "Finalisé";
    if (wasFinalisé !== isFinalisé) {
      setUserDashboardData((prev) => {
        if (!prev?.bilanParAnnee?.[selectedYear]) return prev;
        const yearData = { ...prev.bilanParAnnee[selectedYear] };
        yearData.finalise = Math.max(
          0,
          (yearData.finalise || 0) + (isFinalisé ? 1 : -1)
        );
        yearData.pct =
          yearData.total > 0
            ? Math.round((yearData.finalise / yearData.total) * 100)
            : 0;
        return {
          ...prev,
          bilanParAnnee: { ...prev.bilanParAnnee, [selectedYear]: yearData },
        };
      });
    }

    // 2. On ferme le détail immédiatement — retour au tableau avec les modifs déjà appliquées
    setSelectedClient(null);
    setIsSubmitting(false);

    // 3. Appel API en arrière-plan — PATCH si seulement le status, PUT complet sinon
    try {
      let response;
      if (onlyStatusChanged) {
        response = await authFetch(
          `https://backend-myalfa.vercel.app/api/suiviBilan/${clientId}/${selectedYear}/status`,
          {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ status: newStatus }),
          }
        );
      } else {
        response = await authFetch(
          `https://backend-myalfa.vercel.app/api/suiviBilan/${clientId}/${selectedYear}`,
          {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              ...formData,
              questionnaireMaintenMission: labAnswers,
              labRisque,
              labDecision,
              labSynthese,
              labSignataire,
            }),
          }
        );
      }
      if (!response.ok) throw new Error("Erreur mise à jour");
      showToast("Modifications enregistrées avec succès");
      initialStateRef.current = {
        formData: { ...formData },
        labAnswers: { ...labAnswers },
        labRisque,
        labDecision,
        labSynthese,
        labSignataire,
      };
    } catch (error) {
      console.error(error);
      showToast("Erreur lors de l'enregistrement", "error");
      if (wasFinalisé !== isFinalisé) {
        setUserDashboardData((prev) => {
          if (!prev?.bilanParAnnee?.[selectedYear]) return prev;
          const yearData = { ...prev.bilanParAnnee[selectedYear] };
          yearData.finalise = Math.max(
            0,
            (yearData.finalise || 0) + (isFinalisé ? -1 : 1)
          );
          yearData.pct =
            yearData.total > 0
              ? Math.round((yearData.finalise / yearData.total) * 100)
              : 0;
          return {
            ...prev,
            bilanParAnnee: { ...prev.bilanParAnnee, [selectedYear]: yearData },
          };
        });
      }
      return;
    }

    // 4. Appels secondaires en arrière-plan
    if (newStatus !== capturedInitialStatus) {
      addHistoriqueSuiviBilan(newStatus).catch(console.error);
      setInitialStatus(newStatus);
    }

    if (
      newStatus === "Finalisé" &&
      capturedClient.client.missionJuridique === "oui"
    ) {
      // Optimistic : le statut AGO de cette même année repart à "A faire"
      // instantanément dans suiviJuridiqueData (EtatGlobal.js) — visible
      // immédiatement dans Suivi Juridique AGO sans attendre la réponse
      // serveur ni un changement d'onglet.
      patchSuiviJuridiqueAnnee(clientId, selectedYear, { status: "A faire" });

      authFetch(
        `https://backend-myalfa.vercel.app/api/suiviJuridique/${clientId}/${selectedYear}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ status: "A faire" }),
        }
      )
        .then(() => {
          showToast('Statut AGO réinitialisé à "A faire"');
          refetchAGO();
        })
        .catch((error) => {
          console.error(error);
          showToast(
            "Erreur lors de la réinitialisation du statut AGO",
            "error"
          );
        });

      const emailBody = {
        nomClient: capturedClient.client.nom,
        emailJuridique: capturedClient.client.email_juridique || "",
        selectedYear,
        missionComplementaire: formData.missionComplementaire,
        reserveLegale: formData.missionComplementaire,
        reserveLegaleMontant: formData.reserveLegaleMontant,
        dotee: formData.dotee,
        dispense: formData.dispense,
        sarl: formData.sarl,
        aurtres: formData.aurtres,
        report: formData.report,
        remuneration: formData.remuneration,
        convention: formData.convention,
      };
      authFetch(
        "https://backend-myalfa.vercel.app/api/suiviJuridique/emailBilan",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(emailBody),
        }
      ).catch(console.error);
    }

    fetchUserDashboard().catch(console.error);
  };

  // 👉 Fonction pour changer le statut d’un commentaire
  const handleToggleStatus = async (idCommentaire) => {
    // Lit le statut actuel depuis l'état
    const currentStatus = toggledComments[idCommentaire];

    // Optimistic update
    setToggledComments((prev) => ({
      ...prev,
      [idCommentaire]: !currentStatus,
    }));

    try {
      const response = await authFetch(
        `https://backend-myalfa.vercel.app/api/commentairesSuiviBilan/${idCommentaire}/toggle-status`,
        { method: "PATCH", headers: { "Content-Type": "application/json" } }
      );

      if (!response.ok) throw new Error("Erreur API");

      // Optionnel : synchroniser selectedClient si nécessaire
    } catch (error) {
      console.error("❌", error);

      // rollback en cas d'erreur
      setToggledComments((prev) => ({
        ...prev,
        [idCommentaire]: currentStatus,
      }));

      dialog.alert("Impossible de changer le statut du commentaire.");
    }
  };

  // 👉 Déclare la fonction en dehors du JSX (dans ton composant mais avant le return)
  const handleAddComment = async (
    newCommentType,
    newCommentText,
    selectedClient,
    selectedYear,
    setNewCommentType,
    setNewCommentText,
    setSelectedClient // ajoute ceci pour mettre à jour le state
  ) => {
    const suiviBilanIndex = selectedClient.client.suiviBilans.findIndex(
      (b) => b.annees === selectedYear
    );

    if (suiviBilanIndex === -1) {
      dialog.alert("Aucun suivi bilan trouvé.");
      return;
    }

    setIsAddingComment(true);

    try {
      const response = await authFetch(
        "https://backend-myalfa.vercel.app/api/commentairesSuiviBilan",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            nomComplet: `${utilisateur.lastName} ${utilisateur.name}`,
            commentaire: newCommentText,
            type: newCommentType,
            suiviBilanId: selectedClient.client.suiviBilans[suiviBilanIndex].id,
          }),
        }
      );

      const createdComment = await response.json(); // récupère le commentaire créé

      // Ajoute le commentaire directement dans le state
      const updatedSuiviBilans = [...selectedClient.client.suiviBilans];
      updatedSuiviBilans[suiviBilanIndex] = {
        ...updatedSuiviBilans[suiviBilanIndex],
        commentaires: [
          ...(updatedSuiviBilans[suiviBilanIndex].commentaires || []),
          createdComment,
        ],
      };

      setSelectedClient({
        ...selectedClient,
        client: {
          ...selectedClient.client,
          suiviBilans: updatedSuiviBilans,
        },
      });

      // Réinitialise les inputs
      setNewCommentText("");
      setNewCommentType("");
    } catch (err) {
      console.error(err);
    } finally {
      setIsAddingComment(false);
      // Ne pas mettre setSelectedClient(null)
    }
  };

  const showOverlayDetails = (client, suivi) => {
    setOverlayData({ client, suivi });
  };

  const closeOverlay = () => setOverlayData(null);

  const getRowColor = (status) => {
    switch (status) {
      case "Finalisé":
        return "#e9f6ec";
      case "Supervision en cours":
        return "#fff8dc";
      case "Validé en interne":
        return "#fff8dc";
      case "RDV client à venir":
        return "#fff8dc";
      case "Edition en cours":
        return "#fff8dc";
      case "Révision en cours":
        return "#fff8dc";
      case "À revoir":
        return "#fff8dc";
      case "Bloqué":
        return "#fbe3e3";
      default:
        return "#ffffff";
    }
  };

  const formatDate = (date) => {
    if (!date) return "-";
    return new Date(date).toLocaleDateString("fr-FR");
  };

  const activeFilters = filterRows
    .filter((f) => f.selectedValues?.length > 0)
    .map((f) => [f.column, f.selectedValues]);

  const activeFilterCount = activeFilters.length;

  // 🔹 Fonction pour tout effacer
  const handleClearAllFilters = () => {
    setFilterRows([{ column: "formeSociale", selectedValues: [] }]);
  };

  console.log({ uniqueOptionsByFilter });

  const liasseFields = [
    { label: "Liasse Fiscale", field: "liasseFiscale" },
    { label: "1330-CVAE", field: "cvae1330" },
    { label: "2082-SD", field: "s2082" },
    { label: "2083-SD", field: "s2083" },
    { label: "Art. 44-sexies", field: "sexies44" },
    { label: "2069-RCI", field: "rci2069" },
    { label: "2079", field: "s2079" },
    { label: "DEC-LOYER", field: "decLoyer" },
  ];

  return (
    <div>
      {!selectedClient && (
        <>
          <div className="mb-4">
            <div className="flex items-center justify-between gap-4">
              <div className="relative flex-1 max-w-sm">
                <IoIosSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Filtrer par nom du dossier..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value.toLowerCase())}
                  className="pl-9 h-9 w-full rounded-xl bg-white border border-gray-200 text-sm focus:outline-none focus:border-gray-300"
                />
              </div>
              <div className="flex items-center gap-2">
                <div className="relative">
                  <select
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(Number(e.target.value))}
                    className="h-9 pl-3 pr-7 rounded-lg border border-gray-200 bg-white text-sm font-medium text-gray-700 appearance-none cursor-pointer focus:outline-none focus:border-gray-300"
                  >
                    {availableYears.map((year) => (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    ))}
                  </select>
                  <ChevronDown
                    size={13}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                  />
                </div>
                <button
                  onClick={() => setShowFilterOverlay(!showFilterOverlay)}
                  className="flex items-center gap-2 h-9 px-3 rounded-lg border border-gray-200 bg-white text-sm text-gray-600 font-medium hover:bg-gray-50 transition-colors"
                >
                  <CiFilter className="w-4 h-4" />
                  Filtrer
                </button>
              </div>
            </div>
            {showFilterOverlay && (
              <div
                ref={overlayRef}
                className="fixed top-28 left-3/4 -translate-x-1/2 bg-white border border-slate-200 rounded-lg p-4 shadow-lg z-50 w-2/5 max-h-[80vh] overflow-y-auto"
              >
                <div className="flex justify-between mb-2">
                  <p className="font-semibold">Filtres avancés</p>
                  <button
                    className="text-sm text-slate-600 hover:text-slate-800"
                    onClick={handleResetFilters}
                  >
                    Réinitialiser
                  </button>
                </div>

                {filterRows.map((filter, index) => (
                  <div key={index} className="mb-3">
                    <div className="flex items-center justify-between mb-1">
                      <select
                        value={filter.column}
                        onChange={(e) =>
                          handleFilterChange(index, "column", e.target.value)
                        }
                        className="border border-slate-200 rounded-md p-1 text-sm"
                      >
                        {FILTERABLE_BUTTON_COLUMNS.map((col) => (
                          <option key={col} value={col}>
                            {col}
                          </option>
                        ))}
                      </select>
                      <button
                        onClick={() => handleRemoveFilter(index)}
                        className="text-sm text-red-600 hover:text-red-800"
                      >
                        - Supprimer
                      </button>
                    </div>
                    <div
                      className="flex flex-col ml-2"
                      key={filter.column + index}
                    >
                      {getUniqueValuesForColumn(filter.column).map((value) => (
                        <label key={value}>
                          <input
                            type="checkbox"
                            checked={filter.selectedValues.includes(value)}
                            onChange={() =>
                              handleCheckboxValueChange(index, value)
                            }
                          />
                          {value || "(vide)"}
                        </label>
                      ))}
                    </div>
                  </div>
                ))}

                <button
                  className="text-sm text-slate-700 hover:text-slate-900 mt-2"
                  onClick={handleAddFilter}
                >
                  + Ajouter filtre
                </button>
              </div>
            )}
          </div>

          <div className="grid grid-cols-6 gap-3 mb-6">
            {collaborateursStats.map((collab) => {
              const isActive = selectedCollaborateur === collab.id;

              return (
                <div
                  key={collab.id ?? "NON_AFFECTE"}
                  onClick={() =>
                    setSelectedCollaborateur(isActive ? null : collab.id)
                  }
                  className={`bg-white rounded-lg p-4 border transition-all cursor-pointer ${
                    isActive
                      ? "border-[#b43a3a] "
                      : "border-gray-100 hover:shadow-sm hover:border-gray-200"
                  }`}
                >
                  {/* Header */}
                  <div className="flex items-center gap-1.5 mb-2">
                    <div
                      className="w-7 h-7 rounded-full bg-[#6e1a36] flex items-center justify-center text-white font-bold flex-shrink-0"
                      style={{ fontSize: "0.6rem" }}
                    >
                      {collab.id === NON_AFFECTE
                        ? "NA"
                        : getUserNameByEmail(collab.email || "-")
                            ?.split(" ")
                            .map((n) => n[0])
                            .join("")
                            .toUpperCase()
                            .slice(0, 2)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3
                        className="font-bold text-gray-900 truncate m-0 leading-tight"
                        style={{ fontSize: "0.7rem" }}
                      >
                        {collab.id === NON_AFFECTE
                          ? "Non affecté"
                          : getUserNameByEmail(collab.email || "-")}
                      </h3>
                      <p
                        className="text-gray-400 mt-0.5"
                        style={{ fontSize: "0.63rem" }}
                      >
                        {collab.total} Dossier{collab.total > 1 ? "s" : ""}
                      </p>
                    </div>
                  </div>

                  {/* Stats */}
                  <div className="flex gap-1 mx-5">
                    <div className="flex-1 bg-[#fff8dc] rounded py-1 text-center">
                      <p
                        className="font-bold text-[#8b6a0f] leading-none"
                        style={{ fontSize: "0.78rem" }}
                      >
                        {collab.enCours}
                      </p>
                    </div>
                    <div className="flex-1 bg-[#dcf3e1] rounded py-1 text-center">
                      <p
                        className="font-bold text-[#2f7141] leading-none"
                        style={{ fontSize: "0.78rem" }}
                      >
                        {collab.finalise}
                      </p>
                    </div>
                    <div className="flex-1 bg-[#fbe3e3] rounded py-1 text-center">
                      <p
                        className="font-bold text-[#b43a3a] leading-none"
                        style={{ fontSize: "0.78rem" }}
                      >
                        {collab.enRetard}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {activeFilterCount > 0 && (
            <div className="w-full mt-4 px-3 py-2 flex items-center gap-5 text-xs text-slate-700">
              {/* 🧮 Infos filtres */}
              <div className="flex flex-wrap gap-x-3 gap-y-1 items-center">
                <span className="font-medium px-3 py-2 bg-slate-50 border border-slate-200 rounded-md">
                  {activeFilterCount} filtre{activeFilterCount > 1 ? "s" : ""}{" "}
                  actif
                  {activeFilterCount > 1 ? "s" : ""}
                </span>

                {activeFilters.map(([key, values]) => (
                  <span key={key} className="text-slate-600">
                    <strong>
                      {TABLE_FILTERABLE_COLUMNS[key]?.label || key} :
                    </strong>{" "}
                    {values.join(", ")}
                  </span>
                ))}
              </div>

              {/* ❌ Tout effacer */}
              <button
                onClick={handleClearAllFilters}
                className="text-slate-500 hover:text-[#9B1B5D] text-xs transition-colors px-3 py-2 bg-slate-50 border border-slate-200 rounded-md"
              >
                Tout effacer
              </button>
            </div>
          )}

          <div
            className="mt-6 w-full overflow-x-auto"
            onWheel={(e) => {
              if (e.deltaY !== 0) {
                e.currentTarget.scrollLeft += e.deltaY;
                e.preventDefault();
              }
            }}
            style={{ cursor: "grab" }}
          >
            <table
              className="border border-slate-200 w-full rounded-lg shadow-sm overflow-hidden"
              style={{ fontSize: "0.72rem" }}
            >
              <thead>
                {/* <tr className="bg-gradient-to-r from-[#9B1B5D] to-[#7A1548] text-xs"> */}
                <tr className="bg-[#6e1a36]  text-xs p-5">
                  <th className="px-2 py-3.5 text-white font-semibold text-left">
                    Dossier
                  </th>
                  <FilterableTableTh columnKey="status" label="Status" />
                  <th className="px-2 py-1.5 text-white font-semibold">
                    Echéance
                  </th>
                  <th className="px-2 py-1.5 text-white font-semibold">
                    RDV Client
                  </th>
                  <FilterableTableTh
                    columnKey="liasseFiscale"
                    label="Liasse Fiscale"
                  />
                  <FilterableTableTh columnKey="cvae1330" label="CVAE 1330" />
                  <FilterableTableTh columnKey="rci2069" label="RCI 2069" />
                  <FilterableTableTh columnKey="s2079" label="S2079" />
                  <FilterableTableTh columnKey="s2082" label="S2082" />
                  <FilterableTableTh columnKey="s2083" label="S2083" />
                  <FilterableTableTh columnKey="oga" label="OGA" />
                  <FilterableTableTh
                    columnKey="decLoyer"
                    label="Déclaration Loye"
                  />
                  <FilterableTableTh columnKey="tdfccheck" label="TDFC" />
                  <FilterableTableTh columnKey="s2572check" label="S2572" />
                  <FilterableTableTh columnKey="s3517check" label="S3517" />
                  <FilterableTableTh columnKey="def1329check" label="DEF1329" />

                  <th className="px-2 py-1.5 text-white font-semibold"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {[
                  ...Object.entries(
                    filteredClients.reduce((acc, client) => {
                      const group = client.groupement || null;
                      if (group) {
                        if (!acc[group]) acc[group] = [];
                        acc[group].push(client);
                      }
                      return acc;
                    }, {})
                  ).map(([groupName, clients]) => ({
                    type: "group",
                    name: groupName,
                    clients,
                  })),

                  ...filteredClients
                    .filter((c) => !c.groupement)
                    .map((client) => ({
                      type: "client",
                      name: client.nom,
                      client,
                    })),
                ]
                  .sort((a, b) => a.name.trim().localeCompare(b.name.trim()))
                  .map((item, index) => {
                    if (item.type === "group") {
                      const isOpen = openGroups[item.name] ?? true;

                      return (
                        <React.Fragment key={`group-${item.name}`}>
                          <tr>
                            <td
                              colSpan="16"
                              className="px-2 py-1.5 font-semibold uppercase text-slate-700 relative"
                              onClick={() =>
                                setOpenGroups((prev) => ({
                                  ...prev,
                                  [item.name]: !isOpen,
                                }))
                              }
                            >
                              <div className="flex items-center gap-2">
                                <ChevronDown
                                  size={14}
                                  className={`transition-transform duration-200 ${
                                    openGroups[item.name]
                                      ? "rotate-0"
                                      : "-rotate-90"
                                  }`}
                                />
                                {item.name}
                                <span className="text-slate-500 normal-case">
                                  ({item.clients.length})
                                </span>
                              </div>
                            </td>
                          </tr>

                          {isOpen &&
                            item.clients
                              .sort((a, b) => a.nom.localeCompare(b.nom))
                              .map((client) => {
                                const suivi = client.suiviBilans[0] || null;
                                const isFirstBilanN1 =
                                  suivi?.status === "1er bilan en N+1" ||
                                  suivi?.status === "Fin de mission";
                                const isOverdue =
                                  !!suivi?.dueDate &&
                                  isPast(parseISO(suivi.dueDate)) &&
                                  suivi.status !== "Finalisé";
                                return (
                                  <tr
                                    key={client.id}
                                    data-client-id={client.id}
                                    onClick={() =>
                                      showOverlayDetails(client, suivi || "-")
                                    }
                                    className={`cursor-pointer ${
                                      estBloque(client)
                                        ? "ligne-bloquee"
                                        : isFirstBilanN1
                                        ? "opacity-25 grayscale"
                                        : ""
                                    } ${
                                      animatingId === client.id
                                        ? "todo-row-validating"
                                        : ""
                                    }`}
                                    style={{
                                      backgroundColor: isFirstBilanN1
                                        ? "#f8fafc"
                                        : suivi
                                        ? getRowColor(suivi.status)
                                        : "transparent",
                                    }}
                                  >
                                    <td
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        showClientDetails(client);
                                      }}
                                      className="px-2 py-3.5 font-medium text-slate-900 cursor-pointer hover:text-[#9B1B5D] transition-colors relative overflow-visible"
                                    >
                                      {animatingId === client.id && (
                                        <span className="todo-done-badge">
                                          ✓ Finalisé !
                                        </span>
                                      )}
                                      <div className="flex items-center gap-2">
                                        {isOverdue && (
                                          <AlertCircle className="w-4 h-4 text-red-500 animate-pulse flex-shrink-0" />
                                        )}

                                        <span
                                          className={
                                            isOverdue ? "text-red-900" : ""
                                          }
                                        >
                                          {client.nom}
                                        </span>
                                        {estBloque(client) && <BadgeBloque />}
                                      </div>
                                    </td>

                                    <td className="px-2 py-1.5 text-slate-600">
                                      {suivi ? suivi.status : " "}
                                    </td>
                                    <td className="px-2 py-1.5 text-slate-600">
                                      {formatDate(suivi?.dueDate ?? "")}
                                    </td>
                                    <td className="px-2 py-1.5 text-slate-600">
                                      {suivi
                                        ? formatDateWithoutYear(suivi.rdvClient)
                                        : " "}
                                    </td>

                                    {/* Colonnes indicateurs */}
                                    {/* Colonnes indicateurs */}
                                    {[
                                      "liasseFiscale",
                                      "cvae1330",
                                      "rci2069",
                                      "s2079",
                                      "s2082",
                                      "s2083",
                                      "oga",
                                      "decLoyer",
                                      "tdfccheck",
                                      "s2572check",
                                      "s3517check",
                                      "def1329check",
                                    ].map((field) => (
                                      <td
                                        key={field}
                                        className="px-2 py-1.5 text-center"
                                      >
                                        <div className="flex justify-center items-center">
                                          {/* Condition spéciale S2572 */}
                                          {field === "s2572check" &&
                                          client.regimeFiscal !== "IS RS" &&
                                          client.regimeFiscal !== "IS RN" ? (
                                            <span className="text-xs text-slate-400 font-medium">
                                              N/A
                                            </span>
                                          ) : /* Condition spéciale S3517 */
                                          field === "s3517check" &&
                                            client.regimeTVA !== "RSI" ? (
                                            <span className="text-xs text-slate-400 font-medium">
                                              N/A
                                            </span>
                                          ) : /* Comportement normal */
                                          suivi?.[field] === true ? (
                                            <div
                                              className="w-2.5 h-2.5 rounded-full"
                                              style={{
                                                backgroundColor: "#2bb673",
                                                boxShadow:
                                                  "0 0 0 1px #2bb67340",
                                              }}
                                            />
                                          ) : (
                                            <div className="w-2.5 h-2.5 rounded-full bg-slate-200" />
                                          )}
                                        </div>
                                      </td>
                                    ))}

                                    <td
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleEditClick(
                                          client,
                                          client.suiviBilans[0] || {
                                            annees: selectedYear,
                                          }
                                        );
                                      }}
                                    >
                                      <button
                                        className="flex items-center gap-1 text-xs px-2 py-1 rounded-md
             text-slate-600 hover:text-slate-900
             hover:bg-slate-100 transition-all bg-slate-50 "
                                      >
                                        <ExternalLink className="w-3 h-3" />
                                        <span>Ouvrir</span>
                                      </button>
                                    </td>
                                  </tr>
                                );
                              })}
                        </React.Fragment>
                      );
                    }

                    // Client sans groupement
                    const client = item.client;
                    const suivi = client.suiviBilans[0] || null;
                    const isFirstBilanN1 =
                      suivi?.status === "1er bilan en N+1" ||
                      suivi?.status === "Fin de mission";
                    const isOverdue =
                      !!suivi?.dueDate &&
                      isPast(parseISO(suivi.dueDate)) &&
                      suivi.status !== "Finalisé";
                    return (
                      <tr
                        key={client.id}
                        data-client-id={client.id}
                        onClick={() => showOverlayDetails(client, suivi || "-")}
                        className={`cursor-pointer ${
                          estBloque(client)
                            ? "ligne-bloquee"
                            : isFirstBilanN1
                            ? "opacity-25 grayscale"
                            : ""
                        } ${
                          animatingId === client.id ? "todo-row-validating" : ""
                        }`}
                        style={{
                          backgroundColor: isFirstBilanN1
                            ? "#f8fafc"
                            : suivi
                            ? getRowColor(suivi.status)
                            : "transparent",
                        }}
                      >
                        <td
                          onClick={(e) => {
                            e.stopPropagation();
                            showClientDetails(client);
                          }}
                          className="px-2 py-3.5 font-medium text-slate-900 cursor-pointer hover:text-[#9B1B5D] transition-colors relative overflow-visible"
                        >
                          {animatingId === client.id && (
                            <span className="todo-done-badge">
                              ✓ Finalisé !
                            </span>
                          )}
                          <div className="flex items-center gap-2">
                            {isOverdue && (
                              <AlertCircle className="w-4 h-4 text-red-500 animate-pulse flex-shrink-0" />
                            )}
                            <span className={isOverdue ? "text-red-900" : ""}>
                              {client.nom}
                            </span>
                            {estBloque(client) && <BadgeBloque />}
                          </div>
                        </td>

                        <td className="px-2 py-1.5 text-slate-600">
                          {suivi ? suivi.status : " "}
                        </td>
                        <td className="px-2 py-1.5 text-slate-600">
                          {formatDate(suivi?.dueDate ?? "")}
                        </td>
                        <td className="px-2 py-1.5 text-slate-600">
                          {suivi ? formatDateWithoutYear(suivi.rdvClient) : " "}
                        </td>

                        {/* Colonnes indicateurs */}
                        {/* Colonnes indicateurs */}
                        {[
                          "liasseFiscale",
                          "cvae1330",
                          "rci2069",
                          "s2079",
                          "s2082",
                          "s2083",
                          "oga",
                          "decLoyer",
                          "tdfccheck",
                          "s2572check",
                          "s3517check",
                          "def1329check",
                        ].map((field) => (
                          <td key={field} className="px-2 py-1.5 text-center">
                            <div className="flex justify-center items-center">
                              {/* Condition spéciale S2572 */}
                              {field === "s2572check" &&
                              client.regimeFiscal !== "IS RS" &&
                              client.regimeFiscal !== "IS RN" ? (
                                <span className="text-xs text-slate-400 font-medium">
                                  N/A
                                </span>
                              ) : /* Condition spéciale S3517 */
                              field === "s3517check" &&
                                client.regimeTVA !== "RSI" ? (
                                <span className="text-xs text-slate-400 font-medium">
                                  N/A
                                </span>
                              ) : /* Comportement normal */
                              suivi?.[field] === true ? (
                                <div
                                  className="w-2.5 h-2.5 rounded-full"
                                  style={{
                                    backgroundColor: "#2bb673",
                                    boxShadow: "0 0 0 1px #2bb67340",
                                  }}
                                />
                              ) : (
                                <div className="w-2.5 h-2.5 rounded-full bg-slate-200" />
                              )}
                            </div>
                          </td>
                        ))}

                        <td
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditClick(
                              client,
                              client.suiviBilans[0] || { annees: selectedYear }
                            );
                          }}
                        >
                          <button
                            className="flex items-center gap-1 text-xs px-2 mr-2 py-1 rounded-md
             text-slate-600 hover:text-slate-900
             hover:bg-slate-100 transition-all bg-slate-50"
                          >
                            <ExternalLink className="w-3 h-3" />
                            <span>Ouvrir</span>
                          </button>
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>

          {activeColumnFilter && (
            <div
              ref={filterRef}
              style={{
                position: "fixed",
                top: columnFilterPosition.top,
                left: columnFilterPosition.left,
                zIndex: 10000,
              }}
              className="bg-white border border-slate-200 rounded-lg shadow-lg w-64 max-h-72 flex flex-col"
            >
              <div className="px-3 py-2 border-b font-semibold text-sm">
                {activeColumnFilter &&
                  TABLE_FILTERABLE_COLUMNS[activeColumnFilter]?.label}
              </div>

              <div className="flex-1 overflow-y-auto px-3 py-2">
                {uniqueValues[activeColumnFilter]?.map((value) => {
                  const activeFilter = filterRows.find(
                    (f) => f.column === activeColumnFilter
                  );

                  const checked =
                    activeFilter?.selectedValues.includes(value) ?? false;

                  return (
                    <label
                      key={value}
                      className="flex items-center gap-2 text-sm py-1 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() =>
                          toggleColumnValue(activeColumnFilter, value)
                        }
                      />
                      {value}
                    </label>
                  );
                })}
              </div>

              {/* 🎯 Actions */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  padding: "8px 10px",
                  borderTop: "1px solid #eee",
                }}
              >
                <button
                  onClick={() => handleResetColumnFilter(activeColumnFilter)}
                  style={{
                    fontSize: "0.8rem",
                    padding: "4px 8px",
                    border: "none",
                    borderRadius: "6px",
                    background: "#f0f0f0",
                    cursor: "pointer",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.background = "#e0e0e0")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.background = "#f0f0f0")
                  }
                >
                  Réinitialiser
                </button>

                <button
                  onClick={() => setActiveColumnFilter(null)}
                  style={{
                    fontSize: "0.8rem",
                    padding: "4px 8px",
                    border: "none",
                    borderRadius: "6px",
                    background: "#9B1B5D",
                    color: "#fff",
                    cursor: "pointer",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.background = "#7A1548")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.background = "#9B1B5D")
                  }
                >
                  Appliquer
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {showCreateTodo && (
        <div className="overlayAC">
          <div
            className="inoverlayLead"
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <button
              className="close-button"
              onClick={(e) => {
                e.stopPropagation();
                setShowCreateTodo(false);
              }}
              style={{
                position: "absolute",
                top: "40px",
                right: "420px",
                background: "transparent",
                border: "none",
                fontSize: "28px",
                fontWeight: "bold",
                color: "#999",
                cursor: "pointer",
                transition: "color 0.3s",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.color = "#555")}
              onMouseLeave={(e) => (e.currentTarget.style.color = "#999")}
              aria-label="Fermer"
            >
              &times;
            </button>

            <CreateTodo
              onBack={() => setShowCreateTodo(false)}
              item={selectedClient}
              itemtype={itemtype}
              suiviBilanId={selectedClient?.suivi?.id}
              onClose={() => setShowCreateTodo(false)}
            />
          </div>
        </div>
      )}

      {overlayData && (
        <div className="overlayAC">
          <div className="inoverlayMS">
            <button
              className="close-button"
              onClick={() => setOverlayData(null)}
              style={{
                position: "absolute",
                top: "12px",
                right: "12px",
                background: "transparent",
                border: "none",
                fontSize: "24px",
                cursor: "pointer",
              }}
            >
              <button
                aria-label="Fermer"
                className="p-2 rounded-md text-gray-500 hover:text-gray-800 bg-gray-200 transition"
              >
                <X className="w-4 h-4" />
              </button>
            </button>
            <OverlayDetails
              overlayData={overlayData}
              onClose={closeOverlay}
              formatDateWithoutYear={formatDateWithoutYear}
              renderBool={renderBool}
              selectedYear={selectedYear}
            />
          </div>
        </div>
      )}

      {selectedClient && (
        <div>
          <div>
            <div
              style={{
                display: "flex",
                width: "100%",
                marginTop: "2%",
                overflowX: "hidden",
              }}
            >
              <div className="w-1/4">
                <div className="cursor-pointer shadow-lg px-10 py-4 w-[90%] ml-3 rounded-lg mb-4 flex flex-col justify-center border-slate-200 hover:shadow-md hover:border-slate-200 bg-white">
                  <div
                    onClick={() => setSelectedClient(null)}
                    className="flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900 mb-4"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    Retour aux dossiers
                  </div>
                  <div>
                    <h1 className="text-lg font-bold text-slate-900 mb-1">
                      {selectedClient.client.nom}
                    </h1>
                    <p className="text-sm text-slate-500">
                      Exercice {selectedYear}
                    </p>
                    <div className="space-y-1">
                      <p className="text-xs text-slate-400 mt-1">
                        Date de clôture le :
                      </p>

                      <input
                        type="date"
                        value={
                          formData.dueDate ? formData.dueDate.split("T")[0] : ""
                        }
                        onChange={(e) => {
                          const value = e.target.value;
                          handleChange(
                            "dueDate",
                            value ? `${value}T00:00:00` : null
                          );
                        }}
                        className="text-center text-sm text-gray-900 placeholder-gray-400 border border-gray-300 rounded-md px-3 py-2"
                      />
                    </div>
                  </div>
                </div>
                <br />
                <div className="space-y-2">
                  {sections.map((section) => {
                    const isActive = activeSection === section;
                    const Icon = sectionIcons[section];

                    return (
                      <button
                        key={section}
                        onClick={() => setActiveSection(section)}
                        className={`w-[90%] ml-4 flex items-center gap-3 px-4 py-3 rounded-xl transition-all text-left bg-white
          ${
            isActive
              ? "bg-[#840040]/10 ring-2 ring-[#840040]"
              : "hover:bg-slate-50"
          }`}
                      >
                        <div
                          className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0
            ${isActive ? "bg-[#840040]/10" : "bg-slate-100"}`}
                        >
                          <Icon
                            className={`w-5 h-5
              ${isActive ? "text-[#840040]" : "text-slate-400"}`}
                          />
                        </div>

                        <span
                          className={`text-sm font-medium
            ${isActive ? "text-[#840040]" : "text-slate-600"}`}
                        >
                          {section}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="w-full pl-10">
                {/* Breadcrumb + actions */}
                <div className="flex items-center justify-between mb-5">
                  <div className="flex items-center gap-1.5 text-sm font-semibold">
                    <span className="text-slate-600">
                      #{selectedClient?.client?.nom}
                    </span>
                    <span className="text-slate-400">›</span>
                    <span className="text-[#840040]">{activeSection}</span>
                  </div>
                  <div className="flex gap-3">
                    <select
                      className={`px-4 py-2 rounded-md border text-xs w-48 h-10 ${
                        formData.status === "Révision en cours"
                          ? "bg-yellow-100"
                          : formData.status === "Supervision en cours"
                          ? "bg-yellow-50"
                          : formData.status === "À revoir"
                          ? "bg-orange-400 text-white"
                          : formData.status === "Validé en interne"
                          ? "bg-yellow-200"
                          : formData.status === "RDV client à venir"
                          ? "bg-yellow-50"
                          : formData.status === "Edition en cours"
                          ? "bg-yellow-50"
                          : formData.status === "Finalisé"
                          ? "bg-green-100 text-green-700 border-green-200"
                          : "bg-white"
                      }`}
                      value={formData.status || ""}
                      onChange={(e) => handleChange("status", e.target.value)}
                    >
                      <option value=" ">-- Sélectionner --</option>
                      <option value="En préparation">En préparation</option>
                      <option value="Révision en cours">
                        Révision en cours
                      </option>
                      <option value="Supervision en cours">
                        Supervision en cours
                      </option>
                      <option value="À revoir">À revoir</option>
                      <option value="Validé en interne">
                        Validé en interne
                      </option>
                      <option value="RDV client à venir">
                        RDV client à venir
                      </option>
                      <option value="Edition en cours">Edition en cours</option>
                      <option value="Finalisé">Finalisé</option>
                      <option value="1er bilan en N+1">1er bilan en N+1</option>
                      <option value="Fin de mission">Fin de mission</option>
                    </select>
                    <button
                      onClick={handleSubmit}
                      disabled={isSubmitting}
                      className="flex items-center gap-2 h-10 px-5 rounded-md font-semibold text-sm bg-[#840040] hover:bg-[#6e1a36] text-white disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Save className="w-4 h-4" />
                      {isSubmitting ? "Mise à jour..." : "Enregistrer"}
                    </button>
                    <button
                      onClick={() => setShowCreateTodo(true)}
                      className="flex items-center gap-2 h-10 px-3 rounded-md font-semibold text-sm bg-white text-slate-700 border border-slate-200 hover:bg-slate-50"
                    >
                      Todo
                    </button>
                  </div>
                </div>

                {/* Section header */}
                {activeSection && (
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0 bg-[#840040]/10">
                      {React.createElement(sectionIcons[activeSection], {
                        className: "w-5 h-5 text-[#840040]",
                      })}
                    </div>
                    <div className="flex items-baseline gap-3">
                      <span className="text-xl font-bold text-slate-900">
                        {activeSection}
                      </span>
                      {activeSection === "Diligences LAB" && (
                        <span className="text-slate-400 text-sm font-normal">
                          Revue annuelle de maintien de mission
                        </span>
                      )}
                    </div>
                  </div>
                )}

                {activeSection === "Diligences LAB" && (
                  <div>
                    {/* Questionnaire de maintien de mission */}
                    {(() => {
                      // Questions + conclusion de la revue annuelle.
                      const { faits, total } = labProgression({ labAnswers, labRisque, labDecision, labSynthese });
                      const pct = Math.round((faits / total) * 100);
                      const r = 15.9155;
                      const circ = 2 * Math.PI * r;
                      const dash = (faits / total) * circ;

                      return (
                        <div className="mt-4 ">
                          {/* Card header — fond rose pâle */}
                          <div
                            className="flex items-center gap-4 px-5 py-5 mb-4 rounded-xl"
                            style={{
                              background:
                                "linear-gradient(to right, #f7eaef, #ffffff)",
                            }}
                          >
                            <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 bg-[#840040]">
                              <Shield className="w-5 h-5 text-white" />
                            </div>
                            <span
                              className="flex-1 font-bold text-slate-900"
                              style={{ fontSize: "0.88rem" }}
                            >
                              Questionnaire de maintien de mission — exercice{" "}
                              {selectedYear}
                            </span>
                            <div className="relative w-16 h-16 flex-shrink-0">
                              <svg
                                viewBox="0 0 36 36"
                                className="w-16 h-16"
                                style={{ transform: "rotate(-90deg)" }}
                              >
                                <circle
                                  cx="18"
                                  cy="18"
                                  r={r}
                                  fill="none"
                                  stroke="#e2e8f0"
                                  strokeWidth="2.5"
                                />
                                <circle
                                  cx="18"
                                  cy="18"
                                  r={r}
                                  fill="none"
                                  stroke="#840040"
                                  strokeWidth="2.5"
                                  strokeLinecap="round"
                                  strokeDasharray={`${dash} ${circ}`}
                                />
                              </svg>
                              <div className="absolute inset-0 flex flex-col items-center justify-center">
                                <span
                                  className="font-bold text-slate-800 leading-none"
                                  style={{ fontSize: "0.7rem" }}
                                >
                                  {pct}%
                                </span>
                                <span
                                  className="text-slate-400 leading-none mt-1"
                                  style={{ fontSize: "0.6rem" }}
                                >
                                  {faits} / {total}
                                </span>
                              </div>
                            </div>
                          </div>

                          {/* Table */}
                          <div className="bg-white rounded-xl overflow-hidden border border-gray-200 shadow-md">
                            {/* Columns header */}
                            <div
                              className="grid border-b border-gray-100 px-5 py-2.5 bg-[#fff6ee]"
                              style={{
                                gridTemplateColumns: "1fr 64px 64px 64px 36px",
                              }}
                            >
                              <span
                                className="font-semibold text-slate-500 uppercase tracking-wider"
                                style={{ fontSize: "0.65rem" }}
                              >
                                Question
                              </span>
                              {["OUI", "NON", "N/A"].map((h) => (
                                <span
                                  key={h}
                                  className="font-semibold text-slate-500 uppercase tracking-wider text-center"
                                  style={{ fontSize: "0.65rem" }}
                                >
                                  {h}
                                </span>
                              ))}
                              <span />
                            </div>

                            {/* Question rows */}
                            {LAB_QUESTIONS.map((question, idx) => {
                              const key = `q${idx + 1}`;
                              const answer = labAnswers[key] || null;
                              const isExpanded = labExpandedQ === key;

                              return (
                                <div
                                  key={key}
                                  className="border-b border-gray-100 last:border-0"
                                >
                                  <div
                                    className="grid items-center px-5 py-3.5 gap-2"
                                    style={{
                                      gridTemplateColumns:
                                        "1fr 64px 64px 64px 36px",
                                    }}
                                  >
                                    <div className="flex items-start gap-3 pr-2">
                                      <span
                                        className="flex-shrink-0 px-1.5 py-0.5 rounded bg-[#fff6ee] text-[#6e1a36] font-bold"
                                        style={{ fontSize: "0.62rem" }}
                                      >
                                        {String(idx + 1).padStart(2, "0")}
                                      </span>
                                      <span className="text-slate-700 leading-relaxed text-xs">
                                        {question}
                                      </span>
                                    </div>

                                    {[
                                      {
                                        val: "Oui",
                                        active:
                                          "bg-[#840040] text-white border-[#840040]",
                                      },
                                      {
                                        val: "Non",
                                        active:
                                          "bg-red-500 text-white border-red-500",
                                      },
                                      {
                                        val: "N/A",
                                        active:
                                          "bg-slate-500 text-white border-slate-500",
                                      },
                                    ].map(({ val, active }) => (
                                      <button
                                        key={val}
                                        onClick={() =>
                                          setLabAnswers((p) => ({
                                            ...p,
                                            [key]: p[key] === val ? null : val,
                                          }))
                                        }
                                        className={`h-8 rounded-full text-xs font-medium border transition-all ${
                                          answer === val
                                            ? active
                                            : "bg-white text-slate-600 border-gray-300 hover:border-gray-400"
                                        }`}
                                      >
                                        {val}
                                      </button>
                                    ))}

                                    <button
                                      onClick={() =>
                                        setLabExpandedQ(isExpanded ? null : key)
                                      }
                                      className="w-8 h-8 rounded-full hover:bg-gray-100 flex items-center justify-center transition-colors ml-auto"
                                    >
                                      <ChevronDown
                                        className={`w-4 h-4 text-slate-400 transition-transform ${
                                          isExpanded ? "rotate-0" : "-rotate-90"
                                        }`}
                                      />
                                    </button>
                                  </div>

                                  {isExpanded && (
                                    <div
                                      className="grid grid-cols-2 gap-4 px-5 pb-4 pt-1"
                                      style={{ paddingLeft: "3.5rem" }}
                                    >
                                      {/* Commentaire */}
                                      <div>
                                        <p className="text-xs font-semibold text-slate-500 mb-1.5">
                                          Commentaire
                                        </p>
                                        <textarea
                                          placeholder="Ajouter un commentaire"
                                          value={
                                            labAnswers[`${key}_note`] || ""
                                          }
                                          onChange={(e) =>
                                            setLabAnswers((p) => ({
                                              ...p,
                                              [`${key}_note`]: e.target.value,
                                            }))
                                          }
                                          className="w-full text-xs border border-gray-200 rounded-lg p-2.5 resize-none focus:outline-none focus:border-[#840040] bg-white"
                                          rows={3}
                                        />
                                      </div>
                                      {/* Documents */}
                                      <div>
                                        <p className="text-xs font-semibold text-slate-500 mb-1.5">
                                          Documents
                                        </p>
                                        {labAnswers[`${key}_fileUrl`] ? (
                                          <div className="flex items-center gap-2 px-3 py-2.5 border border-gray-200 rounded-lg bg-white">
                                            <svg
                                              xmlns="http://www.w3.org/2000/svg"
                                              className="w-4 h-4 text-[#840040] flex-shrink-0"
                                              fill="none"
                                              viewBox="0 0 24 24"
                                              stroke="currentColor"
                                              strokeWidth={1.5}
                                            >
                                              <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z"
                                              />
                                            </svg>
                                            <a
                                              href={
                                                labAnswers[`${key}_fileUrl`]
                                              }
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              className="text-xs text-[#840040] hover:underline flex-1 truncate"
                                            >
                                              {labAnswers[`${key}_fileName`] ||
                                                "Voir le fichier"}
                                            </a>
                                            <button
                                              onClick={() =>
                                                setLabAnswers((p) => {
                                                  const n = { ...p };
                                                  delete n[`${key}_fileUrl`];
                                                  delete n[`${key}_fileName`];
                                                  return n;
                                                })
                                              }
                                              className="text-slate-400 hover:text-red-500 transition-colors flex-shrink-0"
                                            >
                                              <X className="w-3.5 h-3.5" />
                                            </button>
                                          </div>
                                        ) : (
                                          <label
                                            className={`flex flex-col items-center justify-center gap-1.5 w-full h-[78px] border border-dashed rounded-lg bg-white cursor-pointer transition-colors ${
                                              labFileUploading[key]
                                                ? "border-[#840040]/50 opacity-70 pointer-events-none"
                                                : "border-gray-300 hover:border-[#840040]/50"
                                            }`}
                                          >
                                            {labFileUploading[key] ? (
                                              <>
                                                <svg
                                                  className="animate-spin w-4 h-4 text-[#840040]"
                                                  viewBox="0 0 24 24"
                                                  fill="none"
                                                >
                                                  <circle
                                                    className="opacity-25"
                                                    cx="12"
                                                    cy="12"
                                                    r="10"
                                                    stroke="currentColor"
                                                    strokeWidth="4"
                                                  />
                                                  <path
                                                    className="opacity-75"
                                                    fill="currentColor"
                                                    d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 00-8 8h4z"
                                                  />
                                                </svg>
                                                <span className="text-xs text-slate-400">
                                                  {labUploadProgress[key] !== undefined
                                                    ? `Upload... ${labUploadProgress[key]}%`
                                                    : "Upload en cours..."}
                                                </span>
                                              </>
                                            ) : (
                                              <>
                                                <svg
                                                  xmlns="http://www.w3.org/2000/svg"
                                                  className="w-4 h-4 text-slate-400"
                                                  fill="none"
                                                  viewBox="0 0 24 24"
                                                  stroke="currentColor"
                                                  strokeWidth={1.5}
                                                >
                                                  <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3"
                                                  />
                                                </svg>
                                                <span className="text-xs text-slate-400">
                                                  Glisser-déposer un fichier
                                                </span>
                                              </>
                                            )}
                                            <input
                                              type="file"
                                              className="hidden"
                                              onChange={async (e) => {
                                                const file =
                                                  e.target.files?.[0];
                                                if (!file) return;
                                                // Affichage immédiat du nom de fichier pendant l'upload
                                                setLabAnswers((p) => ({
                                                  ...p,
                                                  [`${key}_fileName`]:
                                                    file.name,
                                                }));
                                                setLabFileUploading((p) => ({
                                                  ...p,
                                                  [key]: true,
                                                }));
                                                try {
                                                  // Upload direct navigateur → Blob
                                                  // (contourne la limite 4,5 Mo) — plus
                                                  // besoin de passer par le serveur pour ce
                                                  // simple "upload puis renvoie l'URL".
                                                  const url = await uploadFileToBlob(
                                                    file,
                                                    "https://backend-myalfa.vercel.app/api/suiviBilan/upload-token",
                                                    ({ percentage }) =>
                                                      setLabUploadProgress((p) => ({
                                                        ...p,
                                                        [key]: Math.round(percentage),
                                                      }))
                                                  );
                                                  // On ajoute l'URL une fois disponible (nom déjà affiché)
                                                  setLabAnswers((p) => ({
                                                    ...p,
                                                    [`${key}_fileUrl`]: url,
                                                  }));
                                                } catch (err) {
                                                  console.error(
                                                    "Upload LAB doc :",
                                                    err
                                                  );
                                                  // Rollback du nom optimiste en cas d'erreur
                                                  setLabAnswers((p) => {
                                                    const n = { ...p };
                                                    delete n[`${key}_fileName`];
                                                    return n;
                                                  });
                                                  showToast(
                                                    "Erreur lors de l'upload du fichier",
                                                    "error"
                                                  );
                                                } finally {
                                                  setLabFileUploading((p) => ({
                                                    ...p,
                                                    [key]: false,
                                                  }));
                                                  setLabUploadProgress((p) => {
                                                    const n = { ...p };
                                                    delete n[key];
                                                    return n;
                                                  });
                                                  e.target.value = "";
                                                }
                                              }}
                                            />
                                          </label>
                                        )}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })()}

                    {/* Card 1 — titre Conclusion */}
                    <div className="mt-5 rounded-xl overflow-hidden border border-gray-100">
                      <div className="px-5 py-4 bg-gradient-to-r from-[#fdf4f7] to-white">
                        <span className="font-bold text-slate-900 text-sm">
                          Conclusion de la revue annuelle
                        </span>
                      </div>
                    </div>

                    {/* Card 2 — contenu Conclusion */}
                    <div className="mt-3 mb-5 rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
                      {/* Niveau de risque résiduel */}
                      <div className="flex items-center gap-6 px-6 py-4 border-b border-gray-100">
                        <span className="text-sm text-slate-500 w-52 flex-shrink-0">
                          Niveau de risque résiduel
                        </span>
                        <div className="flex items-center gap-2">
                          {[
                            {
                              val: "Faible",
                              active:
                                "bg-green-50 text-green-700 border-green-300 font-semibold",
                            },
                            {
                              val: "Modéré",
                              active:
                                "bg-amber-50 text-amber-700 border-amber-300 font-semibold",
                            },
                            {
                              val: "Élevé",
                              active:
                                "bg-red-50 text-red-600 border-red-300 font-semibold",
                            },
                          ].map(({ val, active }) => (
                            <button
                              key={val}
                              onClick={() =>
                                setLabRisque((p) => (p === val ? null : val))
                              }
                              className={`h-9 px-5 rounded-full text-sm border transition-all ${
                                labRisque === val
                                  ? active
                                  : "bg-white text-slate-600 border-gray-200 hover:border-gray-300"
                              }`}
                            >
                              {val}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Décision sur la mission */}
                      <div className="flex items-center gap-6 px-6 py-4 border-b border-gray-100">
                        <span className="text-sm text-slate-500 w-52 flex-shrink-0">
                          Décision sur la mission
                        </span>
                        <div className="flex items-center gap-2">
                          {[
                            {
                              val: "Maintien",
                              active:
                                "bg-slate-900 text-white border-slate-900 font-semibold",
                            },
                            {
                              val: "Vigilance renforcée",
                              active:
                                "bg-amber-50 text-amber-700 border-amber-300 font-semibold",
                            },
                            {
                              val: "Résiliation",
                              active:
                                "bg-red-50 text-red-600 border-red-300 font-semibold",
                            },
                          ].map(({ val, active }) => (
                            <button
                              key={val}
                              onClick={() =>
                                setLabDecision((p) => (p === val ? null : val))
                              }
                              className={`h-9 px-5 rounded-full text-sm border transition-all ${
                                labDecision === val
                                  ? active
                                  : "bg-white text-slate-600 border-gray-200 hover:border-gray-300"
                              }`}
                            >
                              {val}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Synthèse / motivations */}
                      <div className="px-6 py-4">
                        <p className="text-sm text-slate-500 mb-2">
                          Synthèse / motivations
                        </p>
                        <textarea
                          placeholder="Synthétiser les points-clés de la revue, justifier la décision retenue..."
                          value={labSynthese}
                          onChange={(e) => setLabSynthese(e.target.value)}
                          className="w-full text-sm border border-gray-200 rounded-lg px-4 py-3 resize-y focus:outline-none focus:border-[#840040] bg-white text-slate-700 placeholder-gray-400"
                          rows={3}
                        />
                      </div>
                    </div>
                    {/* Card 3 — titre Validation */}
                    <div className="rounded-xl overflow-hidden border border-gray-100">
                      <div className="px-5 py-4 bg-gradient-to-r from-[#fdf4f7] to-white">
                        <span className="font-bold text-slate-900 text-sm">
                          Validation par l'expert-comptable signataire
                        </span>
                      </div>
                    </div>

                    {/* Card 4 — contenu Validation */}
                    {(() => {
                      const progression = labProgression({ labAnswers, labRisque, labDecision, labSynthese });
                      const isReady = progression.complet && !!labSignataire;
                      const suiviBilanId =
                        selectedClient?.client?.suiviBilans?.find(
                          (s) => s.annees === selectedYear
                        )?.id;
                      const alreadyValidated =
                        !!selectedClient?.client?.suiviBilans?.find(
                          (s) => s.annees === selectedYear
                        )?.labValidatedAt;

                      const expert = users?.find((u) => u.email === labSignataire);
                      const expertNom = expert ? `${expert.lastName} ${expert.name}` : labSignataire || "";
                      const enAttente = revueEnvoi?.statut === "en_attente";

                      // Génère le PDF (« soumise à la validation »), le dépose
                      // dans le stockage puis envoie la revue : mail à
                      // l'expert-comptable, historique LAB, Timeline.
                      const envoyerRevue = async () => {
                        if (!isReady || !suiviBilanId || isValidating) return;
                        setIsValidating(true);
                        try {
                          const envoyeLe = new Date().toISOString();
                          const { blob, fileName } = generateLabRevuePDF({
                            clientNom: selectedClient?.client?.nom,
                            annee: selectedYear,
                            labAnswers,
                            labRisque,
                            labDecision,
                            labSynthese,
                            labSignataire,
                            validatedAt: envoyeLe,
                            users,
                            soumission: true,
                            telecharger: false,
                          });
                          const pdfUrl = await uploadFileToBlob(
                            new File([blob], fileName, { type: "application/pdf" }),
                            "https://backend-myalfa.vercel.app/api/labRevueHistorique/upload-token"
                          );
                          const resp = await authFetch(`https://backend-myalfa.vercel.app/api/suiviBilan/${suiviBilanId}/revue-lab/envoyer`, {
                            method: "POST",
                            headers: { "Content-Type": "application/json" },
                            body: JSON.stringify({ questionnaire: labAnswers, labRisque, labDecision, labSynthese, labSignataire, pdfUrl }),
                          });
                          const data = await resp.json().catch(() => ({}));
                          if (!resp.ok) throw new Error(data.error || "La revue n'a pas pu être envoyée.");

                          // Le serveur a enregistré le formulaire : l'écran et
                          // le tableau Suivi Bilan reprennent les mêmes valeurs.
                          const patch = { questionnaireMaintenMission: labAnswers, labRisque, labDecision, labSynthese, labSignataire, labValidatedAt: null };
                          patchSuiviBilanYear(selectedClient.client.id, selectedYear, patch);
                          setSelectedClient((prev) =>
                            prev
                              ? { ...prev, client: { ...prev.client, suiviBilans: (prev.client.suiviBilans || []).map((x) => (x.annees === selectedYear ? { ...x, ...patch } : x)) } }
                              : prev
                          );
                          if (initialStateRef.current) Object.assign(initialStateRef.current, { labAnswers: { ...labAnswers }, labRisque, labDecision, labSynthese, labSignataire });
                          setRevueEnvoi(data.envoi);
                          // Renvoi : l'envoi resté sans réponse est remplacé.
                          setRevueEnvois((l) => [data.envoi, ...l.map((x) => (x.statut === "en_attente" ? { ...x, statut: "remplacee" } : x))]);
                          setRevueOverlay(false);
                          showStatusToast({
                            status: "success",
                            titre: "Revue envoyée",
                            message: `La revue a été envoyée à ${data.envoi?.expertNom || expertNom}. Vous recevrez un email dès sa réponse.`,
                          });
                        } catch (e) {
                          console.error(e);
                          showStatusToast({ status: "error", titre: "Envoi impossible", message: e.message || "La revue n'a pas pu être envoyée." });
                          // Un envoi dont le mail n'est pas parti reste dans l'historique (avec son PDF).
                          httpClient
                            .get(`https://backend-myalfa.vercel.app/api/suiviBilan/${suiviBilanId}/revue-lab`)
                            .then(({ data }) => Array.isArray(data?.envois) && setRevueEnvois(data.envois))
                            .catch(() => {});
                        } finally {
                          setIsValidating(false);
                        }
                      };

                      return (
                        <div className="mt-3 rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
                          <div className="flex items-center justify-between gap-6 px-6 py-5">
                            <div className="flex-1 space-y-4">
                              {/* Dropdown expert-comptable */}
                              <div className="flex items-center gap-5">
                                <span className="text-sm text-slate-500 w-36 flex-shrink-0 leading-snug">
                                  Expert-comptable
                                  <br />
                                  signataire
                                </span>
                                <div className="relative flex-1 max-w-sm">
                                  <select
                                    value={labSignataire}
                                    onChange={(e) =>
                                      setLabSignataire(e.target.value)
                                    }
                                    className="w-full h-11 pl-4 pr-9 rounded-xl border border-gray-200 text-sm text-slate-700 appearance-none focus:outline-none focus:border-[#840040] bg-white"
                                  >
                                    <option value="">-- Sélectionner --</option>
                                    {users
                                      .filter(
                                        (u) => u.role === "EXPERT_COMPTABLE"
                                      )
                                      .map((u) => (
                                        <option
                                          key={u.id || u.email}
                                          value={u.email}
                                        >
                                          {u.lastName} {u.name}
                                        </option>
                                      ))}
                                  </select>
                                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                                </div>
                              </div>

                              {/* Statut */}
                              <div className="flex items-center gap-3">
                                {revueEnvoi ? (
                                  <div className="flex items-start gap-3">
                                    <div
                                      className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 border ${
                                        revueEnvoi.statut === "validee"
                                          ? "bg-green-50 border-green-200 text-green-600"
                                          : revueEnvoi.statut === "refusee"
                                          ? "bg-red-50 border-red-200 text-red-600"
                                          : "bg-amber-50 border-amber-200 text-amber-500"
                                      }`}
                                    >
                                      {revueEnvoi.statut === "validee" ? <CheckCircle2 className="w-4 h-4" /> : revueEnvoi.statut === "refusee" ? <X className="w-4 h-4" /> : <Mail className="w-4 h-4" />}
                                    </div>
                                    <div className="min-w-0">
                                      <p className="text-sm font-bold text-slate-800">
                                        {revueEnvoi.statut === "validee"
                                          ? `Revue validée par ${revueEnvoi.expertNom}`
                                          : revueEnvoi.statut === "refusee"
                                          ? `Revue non validée par ${revueEnvoi.expertNom}`
                                          : `Revue envoyée à ${revueEnvoi.expertNom}`}
                                      </p>
                                      <p className="text-xs text-slate-400">
                                        {revueEnvoi.decideLe
                                          ? `Le ${dateHeureFr(revueEnvoi.decideLe)} · envoyée le ${dateHeureFr(revueEnvoi.envoyeLe)} par ${revueEnvoi.envoyePar}`
                                          : `Votre revue a été envoyée le ${dateHeureFr(revueEnvoi.envoyeLe)} à ${revueEnvoi.expertNom} · en attente de sa réponse.`}
                                      </p>
                                      {revueEnvoi.statut === "refusee" && revueEnvoi.motif && (
                                        <p className="text-xs text-red-600 mt-1 whitespace-pre-line">Motif : {revueEnvoi.motif}</p>
                                      )}
                                      {revueEnvoi.pdfUrl && (
                                        <a href={revueEnvoi.pdfUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-[#840040] hover:underline mt-1">
                                          <FileText className="w-3.5 h-3.5" /> PDF de la revue envoyée
                                        </a>
                                      )}
                                    </div>
                                  </div>
                                ) : (
                                <>
                                <div
                                  className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                                    alreadyValidated
                                      ? "bg-green-50 border border-green-200"
                                      : "bg-amber-50 border border-amber-200"
                                  }`}
                                >
                                  <span
                                    className={`font-bold text-xs leading-none ${
                                      alreadyValidated
                                        ? "text-green-600"
                                        : "text-amber-500"
                                    }`}
                                  >
                                    {alreadyValidated ? "✓" : "i"}
                                  </span>
                                </div>
                                <div>
                                  <p className="text-sm font-bold text-slate-800">
                                    {progression.complet
                                      ? alreadyValidated
                                        ? "Revue déjà signée"
                                        : "Prêt à valider"
                                      : "En attente de validation"}
                                  </p>
                                  <p className="text-xs text-slate-400">
                                    {alreadyValidated
                                      ? "Une revue a déjà été générée — vous pouvez en créer une nouvelle."
                                      : !progression.complet
                                      ? `Renseigner ${listeFr(progression.manquants)} avant validation.`
                                      : !labSignataire
                                      ? "Choisissez l'expert-comptable signataire."
                                      : !suiviBilanId
                                      ? "Enregistrez d'abord le suivi de cet exercice."
                                      : "Les questions et la conclusion de la revue sont renseignées."}
                                  </p>
                                </div>
                                </>
                                )}
                              </div>
                            </div>

                            {/* Bouton valider */}
                            <button
                              onClick={() => setRevueOverlay(true)}
                              disabled={!isReady || !suiviBilanId || isValidating}
                              title={!isReady ? `Renseignez ${listeFr(progression.manquants) || "l'expert-comptable signataire"} avant l'envoi.` : undefined}
                              className={`flex items-center gap-2 h-11 px-5 rounded-xl font-semibold text-sm transition-all flex-shrink-0 ${
                                isReady && suiviBilanId && !isValidating
                                  ? "bg-[#840040] hover:bg-[#6e1a36] text-white"
                                  : "bg-[#840040]/70 text-white cursor-not-allowed"
                              }`}
                            >
                              {isValidating ? (
                                <>
                                  <svg
                                    className="animate-spin w-4 h-4"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                  >
                                    <circle
                                      className="opacity-25"
                                      cx="12"
                                      cy="12"
                                      r="10"
                                      stroke="currentColor"
                                      strokeWidth="4"
                                    />
                                    <path
                                      className="opacity-75"
                                      fill="currentColor"
                                      d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 00-8 8h4z"
                                    />
                                  </svg>
                                  Validation...
                                </>
                              ) : (
                                <>
                                  <Shield className="w-4 h-4" />
                                  {revueEnvoi ? "Renvoyer la revue" : "Valider et signer la revue"}
                                </>
                              )}
                            </button>
                          </div>

                          {/* Historique des envois de la revue (renvoi illimité) */}
                          {revueEnvois.length > 0 && (
                            <div className="border-t border-gray-100 px-6 py-4">
                              <p className="mb-2 text-xs font-semibold text-slate-500">
                                Historique des envois · {revueEnvois.length} envoi{revueEnvois.length > 1 ? "s" : ""}
                              </p>
                              <div className="divide-y divide-slate-100 rounded-lg border border-slate-200">
                                {revueEnvois.map((x) => {
                                  const st = STATUTS_ENVOI_REVUE[x.statut] || STATUTS_ENVOI_REVUE.en_attente;
                                  return (
                                    <div key={x.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2 text-xs">
                                      <span className="font-semibold tabular-nums text-slate-800">{dateHeureFr(x.envoyeLe)}</span>
                                      <span className="text-slate-500">
                                        à <b className="font-medium text-slate-700">{x.expertNom}</b> · par {x.envoyePar}
                                      </span>
                                      <span className={`rounded-full border px-2 py-0.5 text-[11px] font-semibold ${st.style}`}>
                                        {st.label}
                                        {x.decideLe ? ` le ${dateHeureFr(x.decideLe)}` : ""}
                                      </span>
                                      {x.pdfUrl && (
                                        <a href={x.pdfUrl} target="_blank" rel="noopener noreferrer" className="ml-auto inline-flex items-center gap-1 text-[#840040] hover:underline">
                                          <FileText className="w-3.5 h-3.5" /> PDF
                                        </a>
                                      )}
                                      {x.statut === "refusee" && x.motif && <p className="w-full whitespace-pre-line text-red-600">Motif : {x.motif}</p>}
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}

                          {/* Confirmation avant l'envoi à l'expert-comptable */}
                          {revueOverlay &&
                            createPortal(
                              <div
                                className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 p-4"
                                onMouseDown={(e) => e.target === e.currentTarget && !isValidating && setRevueOverlay(false)}
                              >
                                <div role="dialog" aria-modal="true" aria-labelledby="revue-lab-titre" className="flex max-h-[88vh] w-full max-w-[620px] flex-col rounded-xl border border-slate-200 bg-white shadow-xl">
                                  <div className="flex items-start justify-between gap-3 border-b border-slate-100 px-6 py-4">
                                    <div>
                                      <h3 id="revue-lab-titre" className="flex items-center gap-2 text-base font-bold text-slate-900">
                                        <Send className="w-4 h-4 text-[#840040]" /> {revueEnvoi ? "Renvoyer la revue" : "Envoyer la revue pour validation"}
                                      </h3>
                                      <p className="mt-1 text-sm text-slate-500">
                                        Votre revue annuelle {selectedYear} du dossier <b className="text-slate-700">{selectedClient?.client?.nom}</b> sera envoyée à{" "}
                                        <b className="text-slate-700">{expertNom}</b>{expert?.email ? ` (${expert.email})` : ""}.
                                      </p>
                                    </div>
                                    <button onClick={() => !isValidating && setRevueOverlay(false)} disabled={isValidating} aria-label="Fermer" className="rounded p-1 text-slate-400 hover:text-slate-700 disabled:opacity-40">
                                      <X className="w-4 h-4" />
                                    </button>
                                  </div>

                                  <div className="space-y-4 overflow-y-auto px-6 py-4">
                                    <div className="grid grid-cols-3 gap-2">
                                      {[
                                        ["Niveau de risque", labRisque],
                                        ["Décision", labDecision],
                                        ["Expert-comptable", expertNom],
                                      ].map(([l, v]) => (
                                        <div key={l} className="rounded-lg border border-slate-200 bg-slate-50/60 px-3 py-2">
                                          <p className="text-[11px] text-slate-500">{l}</p>
                                          <p className="text-sm font-semibold text-slate-900 truncate" title={v || ""}>{v || "—"}</p>
                                        </div>
                                      ))}
                                    </div>
                                    <div>
                                      <p className="mb-1 text-xs font-semibold text-slate-500">Synthèse / motivations</p>
                                      <p className="whitespace-pre-line rounded-lg border-l-2 border-[#840040] bg-slate-50 px-3 py-2 text-sm text-slate-700">{labSynthese}</p>
                                    </div>
                                    <div>
                                      <p className="mb-1 text-xs font-semibold text-slate-500">Questionnaire de maintien de mission</p>
                                      <div className="divide-y divide-slate-100 rounded-lg border border-slate-200">
                                        {LAB_QUESTIONS_COURTES.map((q, i) => (
                                          <div key={q} className="flex items-center justify-between gap-3 px-3 py-1.5 text-xs">
                                            <span className="text-slate-600">{i + 1}. {q}</span>
                                            <span className="font-semibold text-slate-900">{labAnswers[`q${i + 1}`] || "—"}</span>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                    <p className="flex items-start gap-2 rounded-lg bg-[#840040]/[0.05] px-3 py-2.5 text-xs text-slate-600">
                                      <Mail className="mt-0.5 w-3.5 h-3.5 flex-shrink-0 text-[#840040]" />
                                      Le PDF de la revue est joint au mail. Vous recevrez un email dès que {expertNom} aura validé ou non la revue.
                                    </p>
                                    {enAttente && (
                                      <p className="flex items-start gap-2 text-xs text-amber-700">
                                        <AlertTriangle className="mt-0.5 w-3.5 h-3.5 flex-shrink-0" />
                                        L'envoi du {dateHeureFr(revueEnvoi.envoyeLe)} à {revueEnvoi.expertNom} est encore sans réponse : son lien ne sera plus valable.
                                      </p>
                                    )}
                                  </div>

                                  <div className="flex justify-end gap-2 border-t border-slate-100 px-6 py-3">
                                    <button onClick={() => setRevueOverlay(false)} disabled={isValidating} className="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-50">
                                      Annuler
                                    </button>
                                    <button onClick={envoyerRevue} disabled={isValidating} className="inline-flex items-center gap-2 rounded-lg bg-[#840040] px-5 py-2 text-sm font-semibold text-white hover:bg-[#6e1a36] disabled:opacity-70">
                                      {isValidating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                                      {isValidating ? "Envoi en cours…" : "Envoyer"}
                                    </button>
                                  </div>
                                </div>
                              </div>,
                              document.body
                            )}
                        </div>
                      );
                    })()}

                    {/* Diligences retenues dans le module KYC/LAB, suivies par exercice */}
                    <DiligencesLabAnnee clientId={selectedClient?.client?.id} annee={selectedYear} />
                  </div>
                )}

                {activeSection === "RDV Client" && (
                  <div className="flex flex-col items-center justify-center h-48 border-2 border-dashed border-gray-300 rounded-lg bg-white p-6 space-y-4">
                    {/* Icône */}
                    <Calendar className="w-16 h-16 text-gray-400" />

                    {/* Label */}
                    <p className="text-gray-500 text-lg font-semibold">
                      RDV Bilan
                    </p>

                    {/* Input datetime */}
                    <input
                      type="date"
                      value={
                        formData.rdvClient
                          ? formData.rdvClient.split("T")[0]
                          : ""
                      }
                      onChange={(e) =>
                        handleChange(
                          "rdvClient",
                          e.target.value
                            ? new Date(e.target.value).toISOString()
                            : null
                        )
                      }
                      className="mt-2 w-64 md:w-80 text-center text-sm text-gray-900 placeholder-gray-400 border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                )}

                {activeSection === "Déclarations fiscales" && (
                  <div className="space-y-4">
                    {/* Déclarations fiscales */}
                    <div className="bg-white ">
                      <div className="bg-gradient-to-r from-[#840040]/5 to-transparent border-b py-5 px-4 mb-4">
                        <h3 className="font-semibold text-sm text-slate-900 m-0">
                          Déclarations fiscales
                        </h3>
                      </div>

                      {/* 3517-SD */}
                      <div
                        className={`grid grid-cols-[10%_5%_25%_5%_5%_30%] items-center gap-2 mt-2 ${
                          selectedClient.client.regimeTVA !== "RSI"
                            ? "opacity-50 pointer-events-none"
                            : ""
                        }`}
                      >
                        <label>3517-SD</label>

                        <input
                          type="checkbox"
                          checked={formData.s3517check || false}
                          onChange={(e) =>
                            handleChange("s3517check", e.target.checked)
                          }
                          disabled={selectedClient.client.regimeTVA !== "RSI"}
                          className="w-4 h-4"
                        />

                        <input
                          type="date"
                          value={
                            formData.s3517 ? formData.s3517.split("T")[0] : ""
                          }
                          onChange={(e) =>
                            handleChange(
                              "s3517",
                              e.target.value
                                ? new Date(e.target.value).toISOString()
                                : null
                            )
                          }
                          disabled={selectedClient.client.regimeTVA !== "RSI"}
                          className="w-full px-3 py-2 border border-slate-300 rounded text-sm"
                        />

                        <label>A/R</label>

                        <input
                          type="checkbox"
                          checked={formData.s3517ar || false}
                          onChange={(e) =>
                            handleChange("s3517ar", e.target.checked)
                          }
                          disabled={selectedClient.client.regimeTVA !== "RSI"}
                          className="w-4 h-4"
                        />

                        {formData.s3517check && (
                          <div className="flex  items-center ">
                            <label className="w-[50%]">Ligne 57 </label>
                            <div className="relative w-full">
                              <input
                                type="text"
                                placeholder="00,00"
                                value={formData.s3517Montant || ""}
                                onChange={(e) =>
                                  handleChange("s3517Montant", e.target.value)
                                }
                                className="w-full px-3 py-2 pr-8 border border-slate-300 rounded text-sm"
                              />
                              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-sm text-gray-500">
                                €
                              </span>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* 2572-SD */}
                      <div
                        className={`grid grid-cols-[10%_5%_25%_5%_5%_30%] items-center gap-2 mt-2 ${
                          selectedClient.client.regimeFiscal !== "IS RS" &&
                          selectedClient.client.regimeFiscal !== "IS RN"
                            ? "opacity-50 pointer-events-none"
                            : ""
                        }`}
                      >
                        <label>2572-SD</label>
                        <input
                          type="checkbox"
                          checked={formData.s2572check || false}
                          onChange={(e) =>
                            handleChange("s2572check", e.target.checked)
                          }
                          disabled={
                            selectedClient.client.regimeFiscal !== "IS RS" &&
                            selectedClient.client.regimeFiscal !== "IS RN"
                          }
                          className="w-4 h-4"
                        />
                        <input
                          type="date"
                          value={
                            formData.s2572 ? formData.s2572.split("T")[0] : ""
                          }
                          onChange={(e) =>
                            handleChange(
                              "s2572",
                              e.target.value
                                ? new Date(e.target.value).toISOString()
                                : null
                            )
                          }
                          disabled={
                            selectedClient.client.regimeFiscal !== "IS RS" &&
                            selectedClient.client.regimeFiscal !== "IS RN"
                          }
                          className="w-full px-3 py-2 border border-slate-300 rounded text-sm"
                        />
                        <label>A/R</label>
                        <input
                          type="checkbox"
                          checked={formData.s2572ar || false}
                          onChange={(e) =>
                            handleChange("s2572ar", e.target.checked)
                          }
                          disabled={
                            selectedClient.client.regimeFiscal !== "IS RS" &&
                            selectedClient.client.regimeFiscal !== "IS RN"
                          }
                          className="w-4 h-4"
                        />
                        {formData.s2572check && (
                          <div className="flex  items-center ">
                            <label className="w-[50%]">Ligne II.C-25</label>
                            <div className="relative w-full">
                              <input
                                type="text"
                                placeholder="00,00"
                                value={formData.s2572Montant || ""}
                                onChange={(e) =>
                                  handleChange("s2572Montant", e.target.value)
                                }
                                className="w-full px-3 py-2 pr-8 border border-slate-300 rounded text-sm"
                              />
                              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-sm text-gray-500">
                                €
                              </span>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* 3573-SD */}
                      <div
                        className={`grid grid-cols-[10%_5%_25%_5%_5%] items-center gap-2 mt-2 ${
                          selectedClient.client.regimeFiscal !== "IS RS" &&
                          selectedClient.client.regimeFiscal !== "IS RN"
                            ? "opacity-50 pointer-events-none"
                            : ""
                        }`}
                      >
                        <label>3573-SD</label>
                        <input
                          type="checkbox"
                          checked={formData.s3573check || false}
                          onChange={(e) =>
                            handleChange("s3573check", e.target.checked)
                          }
                          disabled={
                            selectedClient.client.regimeFiscal !== "IS RS" &&
                            selectedClient.client.regimeFiscal !== "IS RN"
                          }
                          className="w-4 h-4"
                        />
                        <input
                          type="date"
                          value={
                            formData.s3573 ? formData.s3573.split("T")[0] : ""
                          }
                          onChange={(e) =>
                            handleChange(
                              "s3573",
                              e.target.value
                                ? new Date(e.target.value).toISOString()
                                : null
                            )
                          }
                          disabled={
                            selectedClient.client.regimeFiscal !== "IS RS" &&
                            selectedClient.client.regimeFiscal !== "IS RN"
                          }
                          className="w-full px-3 py-2 border border-slate-300 rounded text-sm"
                        />
                        <label>A/R</label>
                        <input
                          type="checkbox"
                          checked={formData.s3573ar || false}
                          onChange={(e) =>
                            handleChange("s3573ar", e.target.checked)
                          }
                          disabled={
                            selectedClient.client.regimeFiscal !== "IS RS" &&
                            selectedClient.client.regimeFiscal !== "IS RN"
                          }
                          className="w-4 h-4"
                        />
                      </div>

                      {/* 1329-DEF */}
                      <div className="grid grid-cols-[10%_5%_25%_5%_5%] items-center gap-2 mt-2">
                        <label>1329-DEF</label>
                        <input
                          type="checkbox"
                          checked={formData.def1329check || false}
                          onChange={(e) =>
                            handleChange("def1329check", e.target.checked)
                          }
                          className="w-4 h-4"
                        />
                        <input
                          type="date"
                          value={
                            formData.def1329
                              ? formData.def1329.split("T")[0]
                              : ""
                          }
                          onChange={(e) =>
                            handleChange(
                              "def1329",
                              e.target.value
                                ? new Date(e.target.value).toISOString()
                                : null
                            )
                          }
                          className="w-full px-3 py-2 border border-slate-300 rounded text-sm"
                        />
                        <label>A/R</label>
                        <input
                          type="checkbox"
                          checked={formData.def1329ar || false}
                          onChange={(e) =>
                            handleChange("def1329ar", e.target.checked)
                          }
                          className="w-4 h-4"
                        />
                      </div>
                    </div>

                    {/* Liasse Fiscale */}
                    <div className="bg-white">
                      <div className="bg-gradient-to-r from-[#840040]/5 to-transparent border-b py-5 px-4 mb-4">
                        <h3 className="font-semibold text-sm text-slate-900 m-0">
                          Liasse Fiscale
                        </h3>
                      </div>
                      <div className="space-y-4">
                        {liasseFields.map(({ label, field }) => (
                          <label
                            key={field}
                            className="flex items-center gap-2 text-sm"
                          >
                            <input
                              type="checkbox"
                              checked={formData[field] || false}
                              onChange={(e) =>
                                handleChange(field, e.target.checked)
                              }
                              className="w-4 h-4"
                            />
                            <span>{label}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    {/* TDFC */}
                    <div className="bg-white">
                      <div className="bg-gradient-to-r from-[#840040]/5 to-transparent border-b py-5 px-4 mb-4">
                        <h3 className="font-semibold text-sm text-slate-900 m-0">
                          TDFC
                        </h3>
                      </div>
                      <div className="grid grid-cols-[7%_5%_25%_5%_5%] items-center gap-2">
                        <label>TDFC</label>
                        <input
                          type="checkbox"
                          checked={formData.tdfccheck || false}
                          onChange={(e) =>
                            handleChange("tdfccheck", e.target.checked)
                          }
                          className="w-4 h-4"
                        />
                        <input
                          type="date"
                          value={
                            formData.tdfc ? formData.tdfc.split("T")[0] : ""
                          }
                          onChange={(e) =>
                            handleChange(
                              "tdfc",
                              e.target.value
                                ? new Date(e.target.value).toISOString()
                                : null
                            )
                          }
                          className="w-full px-3 py-2 border border-slate-300 rounded text-sm"
                        />
                        <label>A/R</label>
                        <input
                          type="checkbox"
                          checked={formData.tdfcar || false}
                          onChange={(e) =>
                            handleChange("tdfcar", e.target.checked)
                          }
                          className="w-4 h-4"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {activeSection === "Vérification seuil" && (
                  <>
                    <div
                      style={{
                        border: "1px solid #ddd",
                        borderRadius: "8px",
                        padding: "12px",
                        marginTop: "10px",
                        backgroundColor: "#fff",
                      }}
                    >
                      {/* HEADER */}
                      <div
                        onClick={() => setAnnexeOpen2(!annexeOpen2)}
                        className="hover:bg-slate-50 transition-colors cursor-pointer p-5 "
                        style={{ userSelect: "none" }}
                      >
                        <div className="flex items-center justify-between">
                          {/* Left: Chevron + Title */}
                          <div className="flex items-center gap-3">
                            <ChevronDown
                              size={20}
                              className="text-slate-400"
                              style={{
                                transform: annexeOpen2
                                  ? "rotate(0deg)"
                                  : "rotate(-90deg)",
                                transition: "transform 0.2s ease",
                              }}
                            />
                            <span className="text-base font-semibold">
                              États financiers - Annexe
                            </span>
                          </div>

                          {/* Right: Counter + Icon */}
                          <div className="flex items-center gap-2">
                            {totalChecked >= 1 ? (
                              <div className="flex items-center gap-1">
                                <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                              </div>
                            ) : (
                              <div className="flex items-center gap-1">
                                <AlertTriangle className="w-5 h-5 text-amber-500" />
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* CONTENT */}
                      {annexeOpen2 && (
                        <div className="mt-2 space-y-6 ml-[5%] mb-10">
                          {/* ---- MICRO ENTREPRISE ---- */}
                          <div className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={safeFormData.microEntreprise || false}
                              onChange={(e) =>
                                handleChange(
                                  "microEntreprise",
                                  e.target.checked
                                )
                              }
                              className="w-4 h-4 accent-indigo-600"
                            />
                            <label className="text-sm font-medium">
                              Dispense d'annexe micro-entreprise (hors holding)
                              :
                            </label>
                          </div>

                          <div className="ml-9 grid grid-cols-[20%_20%] gap-x-4 gap-y-1 text-sm">
                            <p>✓ Total du bilan</p>
                            <p>≤ 350 000 Euro</p>
                            <p>✓ CA net</p>
                            <p>≤ 700 000 Euro</p>
                            <p className="col-span-1">
                              ✓ Nombre moyen de salariés
                            </p>
                            <p>≤ 10</p>
                            <p className="col-span-2 text-xs text-gray-500">
                              (moyenne à la fin de chaque trimestre de l'année
                              civile)
                            </p>
                          </div>

                          {/* ---- PERSONNE PHYSIQUE ---- */}
                          <div className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={safeFormData.personnePhysique || false}
                              onChange={(e) =>
                                handleChange(
                                  "personnePhysique",
                                  e.target.checked
                                )
                              }
                              className="w-4 h-4 accent-indigo-600"
                            />
                            <label className="text-sm font-medium">
                              Dispense d'annexe personne physique :
                            </label>
                          </div>

                          <div className="ml-9 grid grid-cols-[20%_20%] gap-x-4 gap-y-1 text-sm">
                            <p>✓ Personne physique</p>
                            <p>✓ Régime fiscal : RSI</p>
                          </div>

                          {/* ---- PETITE ENTREPRISE ---- */}
                          <div className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={safeFormData.petiteEntreprise || false}
                              onChange={(e) =>
                                handleChange(
                                  "petiteEntreprise",
                                  e.target.checked
                                )
                              }
                              className="w-4 h-4 accent-indigo-600"
                            />
                            <label className="text-sm font-medium">
                              Présentation simplifiée petite-entreprise :
                            </label>
                          </div>

                          <div className="ml-9 grid grid-cols-[20%_20%] gap-x-4 gap-y-1 text-sm">
                            <p>✓ Total du bilan</p>
                            <p>≤ 4 000 000 Euro</p>
                            <p>✓ CA Net</p>
                            <p>≤ 8 000 000 Euro</p>
                            <p>✓ Nombre moyen de salariés</p>
                            <p>≤ 50</p>
                          </div>

                          {/* ---- ANNEXE ABRÉGÉE ---- */}
                          <div className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={safeFormData.abregee || false}
                              onChange={(e) =>
                                handleChange("abregee", e.target.checked)
                              }
                              className="w-4 h-4 accent-indigo-600"
                            />
                            <label className="text-sm font-medium">
                              Présentation annexe abrégée :
                            </label>
                          </div>

                          <div className="ml-9 grid grid-cols-[20%_20%] gap-x-4 gap-y-1 text-sm">
                            <p>✓ Personne morale</p>
                            <p>✓ Régime fiscal : RSI</p>
                          </div>
                        </div>
                      )}
                    </div>
                    <div
                      style={{
                        border: "1px solid #ddd",
                        borderRadius: "8px",
                        padding: "12px",
                        marginTop: "10px",
                        backgroundColor: "#fff",
                      }}
                    >
                      <div
                        onClick={() => setAnnexeOpen(!annexeOpen)}
                        className="hover:bg-slate-50 transition-colors cursor-pointer p-5 "
                        style={{ userSelect: "none" }}
                      >
                        <div className="flex items-center justify-between">
                          {/* Left: Chevron + Title */}
                          <div className="flex items-center gap-3">
                            <ChevronDown
                              size={20}
                              className="text-slate-400"
                              style={{
                                transform: annexeOpen
                                  ? "rotate(0deg)"
                                  : "rotate(-90deg)",
                                transition: "transform 0.2s ease",
                              }}
                            />
                            <span className="text-base font-semibold">
                              Nomination du CAC
                            </span>
                          </div>

                          {/* Right: Counter + Icon */}
                          <div className="flex items-center gap-2">
                            {totalChecked2 >= 1 ? (
                              <div className="flex items-center gap-1">
                                <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                              </div>
                            ) : (
                              <div className="flex items-center gap-1">
                                <AlertTriangle className="w-5 h-5 text-amber-500" />
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      {annexeOpen && (
                        <div className="mt-2 space-y-6 ml-[5%] mb-10">
                          {/* ---- N/A ---- */}
                          <div className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={formData.nA || false}
                              onChange={(e) =>
                                handleChange("nA", e.target.checked)
                              }
                              className="w-4 h-4 accent-indigo-600"
                            />
                            <label className="text-sm font-medium">N/A</label>
                          </div>

                          {/* ---- CAS GENERAL ---- */}
                          <div className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={formData.casGeneral || false}
                              onChange={(e) =>
                                handleChange("casGeneral", e.target.checked)
                              }
                              className="w-4 h-4 accent-indigo-600"
                            />
                            <label className="text-sm font-medium">
                              Cas général (y compris "Petit groupe / Société
                              mère") :
                            </label>
                          </div>

                          <div className="ml-9 grid grid-cols-[20%_20%] gap-x-4 gap-y-1 text-sm">
                            <p>✓ Total du bilan</p>
                            <p>≤ 4 000 000 Euro</p>
                            <p>✓ CA net</p>
                            <p>≤ 8 000 000 Euro</p>
                            <p>✓ Nombre moyen de salariés</p>
                            <p>≤ 50</p>
                          </div>

                          {/* ---- ORGANISME DE FORMATION ---- */}
                          <div className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={formData.organismeFormation || false}
                              onChange={(e) =>
                                handleChange(
                                  "organismeFormation",
                                  e.target.checked
                                )
                              }
                              className="w-4 h-4 accent-indigo-600"
                            />
                            <label className="text-sm font-medium">
                              Organisme de formation :
                            </label>
                          </div>

                          <div className="ml-9 grid grid-cols-[20%_20%] gap-x-4 gap-y-1 text-sm">
                            <p>✓ Total du bilan</p>
                            <p>≤ 230 000 Euro</p>
                            <p>✓ CA net</p>
                            <p>≤ 153 000 Euro</p>
                            <p>✓ Nombre moyen de salariés</p>
                            <p>≤ 3</p>
                          </div>

                          {/* ---- PETIT GROUPE / SOCIETES CONTROLÉES ---- */}
                          <div className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={formData.petitGroupe || false}
                              onChange={(e) =>
                                handleChange("petitGroupe", e.target.checked)
                              }
                              className="w-4 h-4 accent-indigo-600"
                            />
                            <label className="text-sm font-medium">
                              "Petit groupe / Sociétés controlées" :
                            </label>
                          </div>

                          <div className="ml-9 grid grid-cols-[20%_20%] gap-x-4 gap-y-1 text-sm">
                            <p>✓ Total du bilan</p>
                            <p>≤ 2 000 000 Euro</p>
                            <p>✓ CA net</p>
                            <p>≤ 4 000 000 Euro</p>
                            <p>✓ Nombre moyen de salariés</p>
                            <p>≤ 25</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </>
                )}

                {activeSection === "Révision" && (
                  <div className="space-y-4 overflow-hidden">
                    {/* Colonne gauche : Types de commentaires */}
                    <div>
                      <div className="bg-gradient-to-r from-[#840040]/5 to-transparent p-4 rounded-xl border border-[#840040]/10 w-full mb-2">
                        <h3 className="font-semibold text-slate-900 mb-1">
                          Guide de révision
                        </h3>
                        <p className="text-sm text-slate-600">
                          Commentaire par cycle
                        </p>
                      </div>
                      <div className="space-y-3 overflow-y-auto pr-2 border-r border-slate-200">
                        {(() => {
                          const typesList = [
                            { code: "A", name: "Régularité formelle" },
                            { code: "B", name: "Trésorerie / finances" },
                            { code: "C", name: "Achats / fournisseurs" },
                            { code: "D", name: "Charges externes" },
                            { code: "E", name: "Ventes / clients" },
                            { code: "F", name: "Stocks et travaux-en-cours" },
                            { code: "G", name: "Immobilisations" },
                            { code: "H", name: "Personnel" },
                            { code: "I", name: "Etat" },
                            { code: "J", name: "Capitaux propres" },
                            { code: "K", name: "Autres comptes" },
                          ];

                          return typesList.map((t) => {
                            const commentaires =
                              selectedClient?.client?.suiviBilans
                                ?.find((b) => b.annees === selectedYear)
                                ?.commentaires?.filter(
                                  (c) => c.type === t.name
                                ) || [];
                            const hasCommentaires = commentaires.length > 0;
                            const isOpen = selectedCommentType === t.name;

                            return (
                              <div
                                key={t.code}
                                className="border rounded-lg bg-white"
                              >
                                {/* Bouton du type */}
                                <button
                                  onClick={() =>
                                    setSelectedCommentType(
                                      isOpen ? null : t.name
                                    )
                                  }
                                  className="w-full text-left p-3 flex justify-between items-center hover:bg-slate-50"
                                >
                                  <div className="flex items-center gap-2">
                                    <ChevronDown
                                      className={`w-4 h-4 transition-transform ${
                                        isOpen ? "rotate-180" : ""
                                      }`}
                                    />
                                    <span>{t.name}</span>

                                    {hasCommentaires && (
                                      <span className="text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full flex items-center gap-1">
                                        <MessageSquare className="w-3 h-3" />
                                        {commentaires.length}
                                      </span>
                                    )}
                                  </div>

                                  <span
                                    className={`text-xs px-2 py-1 rounded-lg ${
                                      isOpen
                                        ? "bg-[#840040]/10 text-[#840040]"
                                        : "hover:bg-slate-100"
                                    }`}
                                  >
                                    Commenter
                                  </span>
                                </button>

                                {/* CONTENU affiché SOUS le type */}
                                {isOpen && (
                                  <div className="p-4 space-y-4 bg-slate-50 border-t ">
                                    {/* Ajout commentaire */}
                                    <textarea
                                      placeholder="Saisir un commentaire..."
                                      value={newCommentTexts[t.name] || ""}
                                      onChange={(e) =>
                                        setNewCommentTexts({
                                          ...newCommentTexts,
                                          [t.name]: e.target.value,
                                        })
                                      }
                                      className="w-full p-3 rounded-lg border border-slate-300 min-h-[80px] resize-none"
                                    />

                                    <button
                                      onClick={() =>
                                        handleAddComment(
                                          t.name,
                                          newCommentTexts[t.name],
                                          selectedClient,
                                          selectedYear,
                                          () => {},
                                          () =>
                                            setNewCommentTexts({
                                              ...newCommentTexts,
                                              [t.name]: "",
                                            }),
                                          setSelectedClient
                                        )
                                      }
                                      className="w-full bg-[#840040] hover:bg-[#6b0033] text-white py-2 rounded-lg flex items-center justify-center gap-2"
                                    >
                                      <Plus className="w-4 h-4" />
                                      {isAddingComment ? "Envoi..." : "Ajouter"}
                                    </button>

                                    {/* Liste des commentaires */}
                                    <div className="space-y-3">
                                      {commentaires.length === 0 ? (
                                        <div
                                          className="text-center py-6"
                                          style={{ color: "#840040" }}
                                        >
                                          <MessageSquare className="w-8 h-8 mx-auto mb-2 opacity-50" />
                                          <p className="text-sm">
                                            Aucun commentaire pour ce cycle.
                                          </p>
                                        </div>
                                      ) : (
                                        commentaires.map((c) => (
                                          <div
                                            key={c.id}
                                            className="bg-white rounded-lg p-3"
                                            style={{
                                              border: "1px solid #84004030",
                                            }}
                                          >
                                            <div className="flex items-start gap-3">
                                              {/* Avatar */}
                                              <div
                                                className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold text-white flex-shrink-0"
                                                style={{
                                                  backgroundColor: "#840040",
                                                }}
                                              >
                                                {(c.nomComplet || "A")[0]}
                                              </div>

                                              <div className="flex-1">
                                                {/* Header */}
                                                <div className="flex items-baseline justify-between gap-2 mb-1">
                                                  <div className="flex items-center gap-2">
                                                    <span className="font-semibold text-slate-800 text-sm">
                                                      {c.nomComplet ||
                                                        "Anonyme"}
                                                    </span>
                                                  </div>

                                                  {c.createdAt && (
                                                    <span
                                                      className="text-xs flex items-center gap-1"
                                                      style={{
                                                        color: "#840040",
                                                      }}
                                                    >
                                                      <Clock className="w-3 h-3" />
                                                      {new Date(
                                                        c.createdAt
                                                      ).toLocaleString("fr-FR")}
                                                    </span>
                                                  )}
                                                </div>

                                                {/* Commentaire */}
                                                <p className="text-sm text-slate-700 leading-relaxed">
                                                  {c.commentaire}
                                                </p>

                                                {/* Bouton statut */}
                                                <button
                                                  onClick={() =>
                                                    handleToggleStatus(c.id)
                                                  }
                                                  className={`mt-2 px-3 py-1 rounded-full text-xs font-medium text-white transition ${
                                                    toggledComments[c.id] ??
                                                    c.status
                                                      ? "bg-gray-400"
                                                      : "bg-[#840040]"
                                                  }`}
                                                >
                                                  {toggledComments[c.id] ??
                                                  c.status
                                                    ? "Vu"
                                                    : "CRM"}
                                                </button>
                                              </div>
                                            </div>
                                          </div>
                                        ))
                                      )}
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          });
                        })()}
                      </div>
                    </div>
                  </div>
                )}

                {activeSection === "Historique" && (
                  <>
                    <div className="bg-white p-4 ">
                      <div className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-slate-900 mb-3">
                        <div className="w-2 h-2 rounded-full bg-[#8B1538]"></div>
                        Événements cabinet
                      </div>
                      {selectedClient.suivi.historiques &&
                      selectedClient.suivi.historiques.length > 0 ? (
                        selectedClient.suivi.historiques.map((histo) => (
                          <div
                            key={histo.id}
                            style={{
                              position: "relative",
                              paddingLeft: "20px",
                              marginBottom: "15px",
                              borderLeft: "2px solid #cbd5e1",
                            }}
                          >
                            {/* Pastille */}
                            <div
                              style={{
                                position: "absolute",
                                left: "-6px",
                                top: "0",
                                width: "10px",
                                height: "10px",
                                borderRadius: "50%",
                                backgroundColor: "#2563eb",
                                border: "2px solid #fff",
                              }}
                            ></div>

                            {/* Contenu */}
                            <div
                              style={{
                                background: "#eff6ff",
                                borderRadius: "8px",
                                padding: "10px",
                                border: "1px solid #cbd5e1",
                              }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "flex-start",
                                  marginBottom: "4px",
                                }}
                              >
                                <p
                                  style={{
                                    fontSize: "13px",
                                    fontWeight: "600",
                                    color: "#1e40af",
                                  }}
                                >
                                  {histo.titre}
                                </p>
                                <span
                                  style={{
                                    fontSize: "12px",
                                    color: "#1e40af",
                                  }}
                                >
                                  {formatDateWithoutYear(histo.createdAt)}
                                </span>
                              </div>

                              <p
                                style={{
                                  fontSize: "13px",
                                  color: "#1e3a8a",
                                  marginBottom: "4px",
                                }}
                              >
                                {histo.description}
                              </p>
                            </div>
                          </div>
                        ))
                      ) : (
                        <p style={{ fontStyle: "italic", color: "grey" }}>
                          Aucun historique pour l'instant
                        </p>
                      )}
                    </div>
                  </>
                )}

                {activeSection === "CRM" && (
                  <Claude
                    selectedClient={selectedClient}
                    selectedYear={selectedYear}
                    onClientUpdated={majClientCrm}
                  />
                )}

                {activeSection === "Mission complémentaire" && (
                  <div className="mb-5">
                    <div className="space-y-6 mb-5 bg-white">
                      <div className="bg-gradient-to-r from-[#840040]/5 to-transparent border-b py-5 px-4 mb-4">
                        <h3 className="font-semibold text-sm text-slate-900 m-0">
                          Mission complémentaire
                        </h3>
                      </div>
                      <div className="w-full ">
                        <label className="block text-sm font-medium text-gray-700">
                          Commentaire
                        </label>
                        <textarea
                          value={formData.missionComplementaire}
                          onChange={(e) =>
                            handleChange(
                              "missionComplementaire",
                              e.target.value
                            )
                          }
                          placeholder="Commentaire..."
                          className="mt-1.5 block w-full min-h-[80px] rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder-gray-400 shadow-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 focus:outline-none"
                        />
                      </div>
                    </div>
                    <div className="space-y-6 mb-5 bg-white">
                      {/* Header */}
                      <div className="bg-gradient-to-r from-[#840040]/5 to-transparent border-b py-5 px-4">
                        <h3 className="text-sm font-semibold text-slate-900 m-0">
                          Affectation du résultat :
                        </h3>
                      </div>

                      {/* Réserve légale */}
                      <div className="flex flex-wrap items-center gap-4">
                        <div className="flex items-center gap-2 w-full md:w-1/2">
                          <label className="text-sm font-medium">
                            Réserve légale
                          </label>
                          <input
                            type="checkbox"
                            checked={formData.reserveLegale || false}
                            onChange={(e) =>
                              handleChange("reserveLegale", e.target.checked)
                            }
                            className="w-4 h-4 accent-indigo-600"
                          />
                          <input
                            type="text"
                            value={formData.reserveLegaleMontant || ""}
                            onChange={(e) =>
                              handleChange(
                                "reserveLegaleMontant",
                                e.target.value
                              )
                            }
                            className="w-20 border border-gray-300 rounded-md px-2 py-1 text-sm"
                          />
                          <span>€</span>
                          <input
                            type="checkbox"
                            checked={formData.dotee || false}
                            onChange={(e) =>
                              handleChange("dotee", e.target.checked)
                            }
                            className="w-4 h-4 accent-indigo-600 ml-4"
                          />
                          <label className="text-sm font-medium">
                            Entièrment dotée
                          </label>
                        </div>
                      </div>

                      {/* Dividendes */}
                      <p className="ml-0 text-sm font-medium">- Dividendes</p>

                      {/* Dispense prélèvement */}
                      <div className="flex items-center gap-2 ml-4">
                        <label className="text-sm font-medium">
                          Dispense prélèvement forfaitaire non libératoire :
                        </label>
                        <input
                          type="checkbox"
                          checked={formData.dispense || false}
                          onChange={(e) =>
                            handleChange("dispense", e.target.checked)
                          }
                          className="w-4 h-4 accent-indigo-600"
                        />
                      </div>

                      {/* SARL */}
                      <div className="flex items-center gap-2 ml-4">
                        <label className="text-sm font-medium">
                          Si SARL, montant soumis à cotisations sociales :
                        </label>
                        <input
                          type="text"
                          value={formData.sarl || ""}
                          onChange={(e) => handleChange("sarl", e.target.value)}
                          className="w-24 border border-gray-300 rounded-md px-2 py-1 text-sm"
                        />
                      </div>

                      {/* Autres */}
                      <div className="flex items-center gap-2 ml-4">
                        <label className="text-sm font-medium">- Autres</label>
                        <input
                          type="text"
                          value={formData.aurtres || ""}
                          onChange={(e) =>
                            handleChange("aurtres", e.target.value)
                          }
                          className="w-24 border border-gray-300 rounded-md px-2 py-1 text-sm"
                        />
                      </div>

                      {/* Report à nouveau */}
                      <div className="flex items-center gap-2 ml-4">
                        <label className="text-sm font-medium">
                          - Report à nouveau
                        </label>
                        <input
                          type="checkbox"
                          checked={formData.report || false}
                          onChange={(e) =>
                            handleChange("report", e.target.checked)
                          }
                          className="w-4 h-4 accent-indigo-600"
                        />
                      </div>
                    </div>

                    <div className="space-y-6">
                      {/* Header */}
                      <div className="bg-gradient-to-r from-[#840040]/5 to-transparent border-b py-5 px-4">
                        <h3 className="text-sm font-semibold text-slate-900 m-0">
                          Convention :
                        </h3>
                      </div>

                      {/* Rémunération à approuver */}
                      <div className="flex items-center gap-2 ml-4">
                        <label className="text-sm font-medium">
                          Rémunération à approuver :
                        </label>
                        <input
                          type="checkbox"
                          checked={formData.remuneration || false}
                          onChange={(e) =>
                            handleChange("remuneration", e.target.checked)
                          }
                          className="w-4 h-4 accent-indigo-600"
                        />
                      </div>

                      {/* Conventions réglementées à approuver */}
                      <div className="flex items-center gap-2 ml-4">
                        <label className="text-sm font-medium">
                          Conventions réglementées à approuver :
                        </label>
                        <input
                          type="checkbox"
                          checked={formData.convention || false}
                          onChange={(e) =>
                            handleChange("convention", e.target.checked)
                          }
                          className="w-4 h-4 accent-indigo-600"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <FicheClientOverlay
        client={ficheClientData}
        open={ficheClientOpen}
        loading={ficheClientLoading}
        initialTab="general"
        onClose={() => setFicheClientOpen(false)}
        onClientUpdated={(updated) => {
          ficheClientCache.current[updated.id] = updated;
          setFicheClientData(updated);
        }}
      />

      {/* Toast */}
      {toast && (
        <div
          className={`fixed bottom-6 right-6 z-[9999] flex items-center gap-3 px-5 py-3.5 rounded-xl shadow-lg text-sm font-medium transition-all ${
            toast.type === "error"
              ? "bg-red-600 text-white"
              : "bg-[#1a1a1a] text-white"
          }`}
        >
          {toast.type === "error" ? (
            <X className="w-4 h-4 flex-shrink-0" />
          ) : (
            <CheckCircle2 className="w-4 h-4 flex-shrink-0 text-green-400" />
          )}
          {toast.message}
        </div>
      )}
    </div>
  );
};

export default SuiviBilan;
